import { Map } from 'immutable';
import * as i3 from '@ngrx/store';
import { compose, combineReducers } from '@ngrx/store';
import { map, distinctUntilChanged, mergeMap, filter, skip, takeUntil, catchError, first, switchMap, withLatestFrom } from 'rxjs/operators';
import { __decorate, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import * as i2 from '@ngrx/effects';
import { ofType, Effect, EffectsModule } from '@ngrx/effects';
import { of, combineLatest, Observable, throwError } from 'rxjs';
import * as i1 from '@play/api-client';
import { GameApiModule } from '@play/api-client';
import { without, map as map$1, find, forEach, filter as filter$1 } from 'lodash';
import { CommonModule } from '@angular/common';
import { schema, normalize } from 'normalizr';
import { LeagueNamespaceTypesEnum } from '@play/api-typings';

function createErrorObject(error) {
    if (!error || !error.errorCode) {
        console.error(error);
    }
    return {
        code: error ? error.errorCode : 0,
        message: error ? error.message : "Unknown Error",
    };
}

function createLoadingEntryState() {
    return {
        entry: null,
        loading: true,
        error: null,
    };
}

function setToLoading(state) {
    return Object.assign(Object.assign({}, state), { loading: true, error: null });
}
function setToLoadingComplete(state, action) {
    return Object.assign(Object.assign({}, state), { entry: action.payload, loading: false, error: action.error ? action.error : null });
}

function createNestedMultipleLoadReducer(loading, complete) {
    return (state = Map(), action) => {
        const actionTypes = [loading, complete];
        const reducer = createMultipleLoadReducer(loading, complete);
        if (actionTypes.indexOf(action.type) !== -1) {
            const tmpAction = action;
            return state.set(tmpAction.id, reducer(state.get(tmpAction.id), Object.assign(Object.assign({}, tmpAction), { id: tmpAction.id2 })));
        }
        return state;
    };
}
function createMultipleLoadReducer(loading, complete) {
    return (state = Map(), action) => {
        if (action.type === loading) {
            const loadingAction = action;
            return state.set(loadingAction.id, setToLoading(state.get(loadingAction.id) || createLoadingEntryState()));
        }
        if (action.type === complete) {
            const completeAction = action;
            return state.set(completeAction.id, setToLoadingComplete(state.get(completeAction.id), completeAction));
        }
        return state;
    };
}
function createLoadReducer(loading, complete) {
    const initialState = createLoadingEntryState();
    return (state = initialState, action) => {
        if (action.type === loading) {
            return setToLoading(state);
        }
        if (action.type === complete) {
            return setToLoadingComplete(state, action);
        }
        return state;
    };
}

function createLoadSelector(stream) {
    return (id) => {
        return compose(map => map.get(id), stream);
    };
}
function createLoadManySelector(stream) {
    return (ids) => {
        return compose(map => map.filter((value, key) => ids.indexOf(key) !== -1), stream);
    };
}
function exists(data) {
    return Boolean(data);
}
function entryExists(data) {
    return Boolean(data.entry);
}
function entryAvailable(data) {
    return exists(data) && entryExists(data);
}

/**
 * This function coerces a string into a string literal type.
 * Using tagged union types in TypeScript 2.0, this enables
 * powerful typechecking of our reducers.
 *
 * Since every action label passes through this function it
 * is a good place to ensure all of our action labels
 * are unique.
 */
const typeCache = {};
function type(label) {
    if (typeCache[label]) {
        throw new Error(`Action type "${label}" is not unqiue`);
    }
    typeCache[label] = true;
    return label;
}
/**
 * This is a wrapper function for `type` so that we have all the spammy action types.
 * This allows us to filter them out when logging.
 */
const SPAMMY_ACTION_TYPES = [];
function spammyActionType(label) {
    const typeLabel = `${label}`;
    SPAMMY_ACTION_TYPES.push(typeLabel);
    return type(typeLabel);
}
/**
 * This is a wrapper function for `type` so that we have all the loadOnce action types.
 * This allows us to filter them out when logging or skipping them from other reducers.
 */
const LOAD_ONCE_ACTION_TYPES = [];
function loadOnceActionType(label) {
    const typeLabel = `${label}`;
    LOAD_ONCE_ACTION_TYPES.push(typeLabel);
    return spammyActionType(typeLabel);
}

function queueReducers(reducers) {
    return (state, action) => {
        reducers.forEach(reducer => {
            state = reducer(state, action);
        });
        return state;
    };
}
function skipLoadOnceReducer(reducer) {
    return (state, action) => {
        if (SPAMMY_ACTION_TYPES.indexOf(action.type) !== -1) {
            return state;
        }
        return reducer(state, action);
    };
}

function distinct(fn) {
    return (source) => {
        return source.pipe(map(fn), distinctUntilChanged());
    };
}

const TeamActionTypes = {
    SELECT_TEAM: type("[Team] Select"),
    LOAD_TEAM: type("[Team] Load"),
    LOAD_ONCE_TEAM: loadOnceActionType("[Team] Load Once"),
    LOAD_ONCE_MULTIPLE_TEAMS: loadOnceActionType("[Team] Multiple Load Once"),
    LOAD_MULTIPLE_TEAMS: type("[Team] Multiple Load"),
    LOAD_TEAM_COMPLETE: type("[Team] Load Complete"),
    LOAD_MULTIPLE_TEAMS_COMPLETE: type("[Team] Multiple Load Complete"),
    LOAD_MEMBERS: type("[Team] Members Load"),
    LOAD_ONCE_MEMBERS: loadOnceActionType("[Team] Members Load Once"),
    LOAD_MEMBERS_COMPLETE: type("[Team] Members Load Complete"),
    LOAD_MEMBERS_MATCHES: type("[Team] Members Matches Load"),
    LOAD_ONCE_MEMBERS_MATCHES: loadOnceActionType("[Team] Members Matches Load Once"),
    LOAD_MEMBERS_MATCHES_COMPLETE: type("[Team] Members Matches Load Complete"),
};
class SelectTeamAction {
    constructor(teamId) {
        this.teamId = teamId;
        this.type = TeamActionTypes.SELECT_TEAM;
        this.payload = null;
    }
}
class LoadTeamAction {
    constructor(teamId, ignoreCache = false) {
        this.teamId = teamId;
        this.ignoreCache = ignoreCache;
        this.type = TeamActionTypes.LOAD_TEAM;
        this.payload = null;
    }
    get id() {
        return this.teamId;
    }
}
class LoadTeamOnceAction extends LoadTeamAction {
    constructor() {
        super(...arguments);
        this.type = TeamActionTypes.LOAD_ONCE_TEAM;
    }
}
class LoadTeamsAction {
    constructor(teamIds, ignoreCache = false) {
        this.teamIds = teamIds;
        this.ignoreCache = ignoreCache;
        this.type = TeamActionTypes.LOAD_MULTIPLE_TEAMS;
        this.payload = null;
    }
}
class LoadTeamsCompleteAction {
    constructor(teams) {
        this.teams = teams;
        this.type = TeamActionTypes.LOAD_MULTIPLE_TEAMS_COMPLETE;
        this.payload = null;
    }
}
class LoadTeamsOnceAction extends LoadTeamsAction {
    constructor() {
        super(...arguments);
        this.type = TeamActionTypes.LOAD_ONCE_MULTIPLE_TEAMS;
    }
}
class LoadTeamCompleteAction {
    constructor(teamId, payload, error) {
        this.teamId = teamId;
        this.payload = payload;
        this.error = error;
        this.type = TeamActionTypes.LOAD_TEAM_COMPLETE;
    }
    get id() {
        return this.teamId;
    }
}
class LoadMembersAction {
    constructor(teamId, ignoreCache = false) {
        this.teamId = teamId;
        this.ignoreCache = ignoreCache;
        this.type = TeamActionTypes.LOAD_MEMBERS;
        this.payload = null;
    }
    get id() {
        return this.teamId;
    }
}
class LoadMembersOnceAction extends LoadMembersAction {
    constructor() {
        super(...arguments);
        this.type = TeamActionTypes.LOAD_ONCE_MEMBERS;
    }
}
class LoadMembersCompleteAction {
    constructor(teamId, payload, error) {
        this.teamId = teamId;
        this.payload = payload;
        this.error = error;
        this.type = TeamActionTypes.LOAD_MEMBERS_COMPLETE;
    }
    get id() {
        return this.teamId;
    }
}
class LoadMembersMatchesAction {
    constructor(memberId, matchesQuery, ignoreCache = false) {
        this.memberId = memberId;
        this.matchesQuery = matchesQuery;
        this.ignoreCache = ignoreCache;
        this.type = TeamActionTypes.LOAD_MEMBERS_MATCHES;
        this.payload = null;
    }
    get id() {
        return this.memberId;
    }
    get query() {
        return this.matchesQuery;
    }
}
class LoadMembersMatchesOnceAction extends LoadMembersMatchesAction {
    constructor() {
        super(...arguments);
        this.type = TeamActionTypes.LOAD_ONCE_MEMBERS_MATCHES;
    }
}
class LoadMembersMatchesCompleteAction {
    constructor(memberId, payload, error) {
        this.memberId = memberId;
        this.payload = payload;
        this.error = error;
        this.type = TeamActionTypes.LOAD_MEMBERS_MATCHES_COMPLETE;
    }
    get id() {
        return this.memberId;
    }
}

const loadTeamsReducer = createMultipleLoadReducer(TeamActionTypes.LOAD_TEAM, TeamActionTypes.LOAD_TEAM_COMPLETE);
const reducers$j = combineReducers({
    teams: queueReducers([
        loadTeamsReducer,
        (state, action) => {
            if (action.type === TeamActionTypes.LOAD_MULTIPLE_TEAMS) {
                const newAction = action;
                newAction.teamIds.forEach(teamId => {
                    state = loadTeamsReducer(state, new LoadTeamAction(teamId));
                });
            }
            if (action.type === TeamActionTypes.LOAD_MULTIPLE_TEAMS_COMPLETE) {
                const newAction = action;
                newAction.teams.forEach(team => {
                    state = loadTeamsReducer(state, new LoadTeamCompleteAction(+team.id, team, null));
                });
            }
            return state;
        },
    ]),
    members: createMultipleLoadReducer(TeamActionTypes.LOAD_MEMBERS, TeamActionTypes.LOAD_MEMBERS_COMPLETE),
    matches: createMultipleLoadReducer(TeamActionTypes.LOAD_MEMBERS_MATCHES, TeamActionTypes.LOAD_MEMBERS_MATCHES_COMPLETE),
});
function teamReducer(state, action) {
    return reducers$j(state, action);
}

function getMembers(state) {
    return state.members;
}
function getTeamEntity(state) {
    return state.teams;
}
function getTeamState(state) {
    return state.team;
}
function getTeamMemberMatchEntity(state) {
    return state.matches;
}
function getTeamMemberMatchInfo(memberId) {
    return (state) => {
        if (!state) {
            return null;
        }
        return state.get(memberId);
    };
}
const getTeam$1 = createLoadSelector(compose(getTeamEntity, getTeamState));
const getTeams$1 = createLoadManySelector(compose(getTeamEntity, getTeamState));
const getTeamMembers = createLoadSelector(compose(getMembers, getTeamState));
const getManyTeamMembers = createLoadManySelector(compose(getMembers, getTeamState));
const getTeamMembersMatches = () => (compose(getTeamMemberMatchEntity, getTeamState));
const getTeamMemberMatches = (memberId) => (compose(getTeamMemberMatchInfo(memberId), getTeamMemberMatchEntity, getTeamState));

class TeamEffects {
    constructor(teamResource, playerResource, actions$, state$) {
        this.teamResource = teamResource;
        this.playerResource = playerResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadTeam$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_TEAM), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_TEAM), filter((action2) => action.teamId === action2.teamId), skip(1));
            return this.teamResource.getTeam(action.teamId).pipe(takeUntil(nextSearch$), map(loadedTeam => new LoadTeamCompleteAction(action.teamId, loadedTeam)), catchError(error => of(new LoadTeamCompleteAction(action.teamId, null, createErrorObject(error)))));
        }));
        this.loadTeams$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_MULTIPLE_TEAMS), mergeMap((action) => {
            if (!action.teamIds.length) {
                return [];
            }
            return this.teamResource.getTeams(action.teamIds).pipe(map(teams => new LoadTeamsCompleteAction(teams)), catchError(error => {
                return action.teamIds.map(id => new LoadTeamCompleteAction(id, null, createErrorObject(error)));
            }));
        }));
        this.loadTeamsOnce$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_ONCE_MULTIPLE_TEAMS), mergeMap((action) => {
            if (!action.teamIds.length) {
                return [];
            }
            return combineLatest(action.teamIds.map(id => {
                return this.state$.select(getTeam$1(id)).pipe(first(), map(team => (exists(team) ? undefined : id)));
            })).pipe(map(teamIds => without(teamIds, undefined)), first());
        }), filter(teamIds => Boolean(teamIds.length)), map(teamIds => new LoadTeamsAction(teamIds)));
        this.selectTeam$ = this.actions$.pipe(ofType(TeamActionTypes.SELECT_TEAM), mergeMap((action) => {
            return [new LoadTeamAction(action.teamId), new LoadMembersAction(action.teamId)];
        }));
        this.loadTeamOnce$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_ONCE_TEAM), mergeMap((action) => {
            return this.state$.select(getTeam$1(action.teamId)).pipe(first(), filter(team => !Boolean(team)), map(team => action));
        }), mergeMap(action => [new LoadTeamAction(action.teamId)]));
        this.loadMembers$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_MEMBERS), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_MEMBERS), filter((action2) => action.teamId === action2.teamId), skip(1));
            return this.teamResource.getMembersOfTeam(action.teamId).pipe(takeUntil(nextSearch$), map(members => new LoadMembersCompleteAction(action.teamId, members)), catchError(error => of(new LoadMembersCompleteAction(action.teamId, null, createErrorObject(error)))));
        }));
        this.loadMembersOnce$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_ONCE_MEMBERS), mergeMap((action) => {
            return this.state$.select(getTeamMembers(action.teamId)).pipe(first(), filter(members => !Boolean(members)), map(members => action));
        }), mergeMap(action => [new LoadMembersAction(action.teamId)]));
        this.loadMembersMatches$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_MEMBERS_MATCHES), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_MEMBERS_MATCHES), filter((action2) => action.memberId === action2.memberId), skip(1));
            return this.playerResource.getMatchesOfPlayer(action.memberId, action.matchesQuery).pipe(takeUntil(nextSearch$), map(members => new LoadMembersMatchesCompleteAction(action.memberId, members.map(({ id }) => id))), catchError(error => of(new LoadMembersMatchesCompleteAction(action.memberId, null, createErrorObject(error)))));
        }));
        this.loadMembersMatchesOnce$ = this.actions$.pipe(ofType(TeamActionTypes.LOAD_ONCE_MEMBERS_MATCHES), mergeMap((action) => {
            return this.state$.select(getTeamMemberMatches(action.memberId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadMembersMatchesAction(action.memberId, action.matchesQuery)]));
    }
}
TeamEffects.ɵfac = function TeamEffects_Factory(t) { return new (t || TeamEffects)(i0.ɵɵinject(i1.TeamResource), i0.ɵɵinject(i1.PlayerResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
TeamEffects.ɵprov = i0.ɵɵdefineInjectable({ token: TeamEffects, factory: TeamEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], TeamEffects.prototype, "loadTeam$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], TeamEffects.prototype, "loadTeams$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], TeamEffects.prototype, "loadTeamsOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], TeamEffects.prototype, "selectTeam$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], TeamEffects.prototype, "loadTeamOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], TeamEffects.prototype, "loadMembers$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], TeamEffects.prototype, "loadMembersOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], TeamEffects.prototype, "loadMembersMatches$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], TeamEffects.prototype, "loadMembersMatchesOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TeamEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.TeamResource }, { type: i1.PlayerResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadTeam$: [], loadTeams$: [], loadTeamsOnce$: [], selectTeam$: [], loadTeamOnce$: [], loadMembers$: [], loadMembersOnce$: [], loadMembersMatches$: [], loadMembersMatchesOnce$: [] }); })();

class TeamModule {
}
TeamModule.ɵfac = function TeamModule_Factory(t) { return new (t || TeamModule)(); };
TeamModule.ɵmod = i0.ɵɵdefineNgModule({ type: TeamModule });
TeamModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([TeamEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(TeamModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TeamModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([TeamEffects])],
            }]
    }], null, null); })();

const PubgActionTypes = {
    LOAD_MATCH_SETTINGS: type("[Pubg] Match Settings Load"),
    LOAD_ONCE_MATCH_SETTINGS: loadOnceActionType("[Pubg] Match Settings Load Once"),
    LOAD_MATCH_SETTINGS_COMPLETE: type("[Pubg] Match Settings Load Complete"),
    LOAD_MATCH_METADATA: type("[Pubg] Match Metadata Load"),
    LOAD_ONCE_MATCH_METADATA: loadOnceActionType("[Pubg] Match Metadata Load Once"),
    LOAD_MATCH_METADATA_COMPLETE: type("[Pubg] Match Metadata Load Complete"),
    LOAD_LEAGUE_SETTINGS: type("[Pubg] League Settings Load"),
    LOAD_ONCE_LEAGUE_SETTINGS: loadOnceActionType("[Pubg] League Settings Load Once"),
    LOAD_LEAGUE_SETTINGS_COMPLETE: type("[Pubg] League Settings Load Complete"),
    LOAD_LEAGUE_METADATA: type("[Pubg] League Metadata Load"),
    LOAD_ONCE_LEAGUE_METADATA: loadOnceActionType("[Pubg] League Metadata Load Once"),
    LOAD_LEAGUE_METADATA_COMPLETE: type("[Pubg] League Metadata Load Complete"),
};
// Match
class LoadMatchSettingsAction {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = PubgActionTypes.LOAD_MATCH_SETTINGS;
        this.payload = null;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchSettingsOnceAction extends LoadMatchSettingsAction {
    constructor() {
        super(...arguments);
        this.type = PubgActionTypes.LOAD_ONCE_MATCH_SETTINGS;
    }
}
class LoadMatchSettingsCompleteAction {
    constructor(matchId, payload, error) {
        this.matchId = matchId;
        this.payload = payload;
        this.error = error;
        this.type = PubgActionTypes.LOAD_MATCH_SETTINGS_COMPLETE;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchMetaDataAction$2 {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = PubgActionTypes.LOAD_MATCH_METADATA;
        this.payload = null;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchMetaDataOnceAction$2 extends LoadMatchMetaDataAction$2 {
    constructor() {
        super(...arguments);
        this.type = PubgActionTypes.LOAD_ONCE_MATCH_METADATA;
    }
}
class LoadMatchMetaDataCompleteAction$2 {
    constructor(matchId, payload, error) {
        this.matchId = matchId;
        this.payload = payload;
        this.error = error;
        this.type = PubgActionTypes.LOAD_MATCH_METADATA_COMPLETE;
    }
    get id() {
        return this.matchId;
    }
}

function getGameintegrationState(state) {
    return state.gameintegration;
}

/**
 * Workaround since Rootstate needs to be imported
 * @see https://github.com/Microsoft/TypeScript/issues/9944
 */
const getGiState$3 = getGameintegrationState;
function getPubgState(state) {
    return state.pubg;
}
// Match
function getMatchMetaDataEntity$2(state) {
    return state.matchMetaData;
}
function getMatchSettingsEntity(state) {
    return state.matchSettings;
}
const getMatchSettings = createLoadSelector(compose(getMatchSettingsEntity, getPubgState, getGiState$3));
const getMatchMetaData$2 = createLoadSelector(compose(getMatchMetaDataEntity$2, getPubgState, getGiState$3));

const reducers$i = combineReducers({
    matchMetaData: createMultipleLoadReducer(PubgActionTypes.LOAD_MATCH_METADATA, PubgActionTypes.LOAD_MATCH_METADATA_COMPLETE),
    matchSettings: createMultipleLoadReducer(PubgActionTypes.LOAD_MATCH_SETTINGS, PubgActionTypes.LOAD_MATCH_SETTINGS_COMPLETE),
    leagueMetaData: createMultipleLoadReducer(PubgActionTypes.LOAD_LEAGUE_METADATA, PubgActionTypes.LOAD_LEAGUE_METADATA_COMPLETE),
    leagueSettings: createMultipleLoadReducer(PubgActionTypes.LOAD_LEAGUE_SETTINGS, PubgActionTypes.LOAD_LEAGUE_SETTINGS_COMPLETE),
});
function pubgReducer(state, action) {
    return reducers$i(state, action);
}

class PubgMatchEffects {
    constructor(pubgMatchResource, actions$, state$) {
        this.pubgMatchResource = pubgMatchResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadMatchSettings$ = this.actions$.pipe(ofType(PubgActionTypes.LOAD_MATCH_SETTINGS), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(PubgActionTypes.LOAD_MATCH_SETTINGS), filter((action2) => action.matchId === action2.matchId), skip(1));
            return this.pubgMatchResource.getSettingsOfMatch(action.matchId).pipe(takeUntil(nextSearch$), map(settings => new LoadMatchSettingsCompleteAction(action.matchId, settings)), catchError(error => of(new LoadMatchSettingsCompleteAction(action.matchId, null, createErrorObject(error)))));
        }));
        this.loadMatchMetaData$ = this.actions$.pipe(ofType(PubgActionTypes.LOAD_MATCH_METADATA), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(PubgActionTypes.LOAD_MATCH_METADATA), filter((action2) => action.matchId === action2.matchId), skip(1));
            return this.pubgMatchResource.getMetaDataOfMatch(action.matchId).pipe(takeUntil(nextSearch$), map(metaData => new LoadMatchMetaDataCompleteAction$2(action.matchId, metaData)), catchError(error => of(new LoadMatchMetaDataCompleteAction$2(action.matchId, null, createErrorObject(error)))));
        }));
        this.loadMatchSettingsOnce$ = this.actions$.pipe(ofType(PubgActionTypes.LOAD_ONCE_MATCH_SETTINGS), mergeMap((action) => {
            return this.state$.select(getMatchSettings(action.matchId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadMatchSettingsAction(action.matchId)]));
        this.loadMatchMetaDataOnce$ = this.actions$.pipe(ofType(PubgActionTypes.LOAD_ONCE_MATCH_METADATA), mergeMap((action) => {
            return this.state$.select(getMatchMetaData$2(action.matchId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadMatchMetaDataAction$2(action.matchId)]));
    }
}
PubgMatchEffects.ɵfac = function PubgMatchEffects_Factory(t) { return new (t || PubgMatchEffects)(i0.ɵɵinject(i1.PubgMatchResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
PubgMatchEffects.ɵprov = i0.ɵɵdefineInjectable({ token: PubgMatchEffects, factory: PubgMatchEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PubgMatchEffects.prototype, "loadMatchSettings$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PubgMatchEffects.prototype, "loadMatchMetaData$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PubgMatchEffects.prototype, "loadMatchSettingsOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PubgMatchEffects.prototype, "loadMatchMetaDataOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgMatchEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.PubgMatchResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadMatchSettings$: [], loadMatchMetaData$: [], loadMatchSettingsOnce$: [], loadMatchMetaDataOnce$: [] }); })();

class PubgModule {
}
PubgModule.ɵfac = function PubgModule_Factory(t) { return new (t || PubgModule)(); };
PubgModule.ɵmod = i0.ɵɵdefineNgModule({ type: PubgModule });
PubgModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([PubgMatchEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PubgModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([PubgMatchEffects])],
            }]
    }], null, null); })();

const PubgmActionTypes = {
    LOAD_MATCH_METADATA: type("[PUBG MOBILE] Match Metadata Load"),
    LOAD_ONCE_MATCH_METADATA: loadOnceActionType("[PUBG MOBILE] Match Metadata Load Once"),
    LOAD_MATCH_METADATA_COMPLETE: type("[PUBG MOBILE] Match Metadata Load Complete"),
    LOAD_ROOM_DATA: type("[PUBG MOBILE] Room Data Load"),
    LOAD_ONCE_ROOM_DATA: loadOnceActionType("[PUBG MOBILE] Room Data Load Once"),
    LOAD_ROOM_DATA_COMPLETE: type("[PUBG MOBILE] Room Data Load Complete"),
};
// Match
class LoadMatchMetaDataAction$1 {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = PubgmActionTypes.LOAD_MATCH_METADATA;
        this.payload = null;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchMetaDataOnceAction$1 extends LoadMatchMetaDataAction$1 {
    constructor() {
        super(...arguments);
        this.type = PubgmActionTypes.LOAD_ONCE_MATCH_METADATA;
    }
}
class LoadMatchMetaDataCompleteAction$1 {
    constructor(matchId, payload, error) {
        this.matchId = matchId;
        this.payload = payload;
        this.error = error;
        this.type = PubgmActionTypes.LOAD_MATCH_METADATA_COMPLETE;
    }
    get id() {
        return this.matchId;
    }
}
// Room
class LoadRoomDataAction {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = PubgmActionTypes.LOAD_ROOM_DATA;
        this.payload = null;
    }
    get id() {
        return this.matchId;
    }
}
class LoadRoomDataOnceAction extends LoadRoomDataAction {
    constructor() {
        super(...arguments);
        this.type = PubgmActionTypes.LOAD_ONCE_ROOM_DATA;
    }
}
class LoadRoomDataCompleteAction {
    constructor(matchId, payload, error) {
        this.matchId = matchId;
        this.payload = payload;
        this.error = error;
        this.type = PubgmActionTypes.LOAD_ROOM_DATA_COMPLETE;
    }
    get id() {
        return this.matchId;
    }
}

/**
 * Workaround since Rootstate needs to be imported
 * @see https://github.com/Microsoft/TypeScript/issues/9944
 */
const getGiState$2 = getGameintegrationState;
function getPubgmState(state) {
    return state.pubgm;
}
// Match
function getMatchMetaDataEntity$1(state) {
    return state.matchMetaData;
}
function getRoomsDataEntity(state) {
    return state.rooms;
}
const getMatchMetaData$1 = createLoadSelector(compose(getMatchMetaDataEntity$1, getPubgmState, getGiState$2));
const getRoomData = createLoadSelector(compose(getRoomsDataEntity, getPubgmState, getGiState$2));

const reducers$h = combineReducers({
    matchMetaData: createMultipleLoadReducer(PubgmActionTypes.LOAD_MATCH_METADATA, PubgmActionTypes.LOAD_MATCH_METADATA_COMPLETE),
    rooms: createMultipleLoadReducer(PubgmActionTypes.LOAD_ROOM_DATA, PubgmActionTypes.LOAD_ROOM_DATA_COMPLETE),
});
function pubgmReducer(state, action) {
    return reducers$h(state, action);
}

class PubgmMatchEffects {
    constructor(pubgMobileMatchResource, actions$, state$) {
        this.pubgMobileMatchResource = pubgMobileMatchResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadMatchMetaData$ = this.actions$.pipe(ofType(PubgmActionTypes.LOAD_MATCH_METADATA), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(PubgmActionTypes.LOAD_MATCH_METADATA), filter((action2) => action.matchId === action2.matchId), skip(1));
            return this.pubgMobileMatchResource.getMetaDataOfMatch(action.matchId).pipe(takeUntil(nextSearch$), map(metaData => new LoadMatchMetaDataCompleteAction$1(action.matchId, metaData)), catchError(error => of(new LoadMatchMetaDataCompleteAction$1(action.matchId, null, createErrorObject(error)))));
        }));
        this.loadRoomData$ = this.actions$.pipe(ofType(PubgmActionTypes.LOAD_ROOM_DATA), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(PubgmActionTypes.LOAD_ROOM_DATA), filter((action2) => action.matchId === action2.matchId), skip(1));
            return this.pubgMobileMatchResource.getPUBGMobileRoom(action.matchId).pipe(takeUntil(nextSearch$), map(roomData => new LoadRoomDataCompleteAction(action.matchId, roomData)), catchError(error => of(new LoadRoomDataCompleteAction(action.matchId, null, createErrorObject(error)))));
        }));
        this.loadMatchMetaDataOnce$ = this.actions$.pipe(ofType(PubgmActionTypes.LOAD_ONCE_MATCH_METADATA), mergeMap((action) => {
            return this.state$.select(getMatchMetaData$1(action.matchId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadMatchMetaDataAction$1(action.matchId)]));
        this.loadRoomDataOnce$ = this.actions$.pipe(ofType(PubgmActionTypes.LOAD_ONCE_ROOM_DATA), mergeMap((action) => {
            return this.state$.select(getRoomData(action.matchId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadRoomDataOnceAction(action.matchId)]));
    }
}
PubgmMatchEffects.ɵfac = function PubgmMatchEffects_Factory(t) { return new (t || PubgmMatchEffects)(i0.ɵɵinject(i1.PubgMobileMatchResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
PubgmMatchEffects.ɵprov = i0.ɵɵdefineInjectable({ token: PubgmMatchEffects, factory: PubgmMatchEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PubgmMatchEffects.prototype, "loadMatchMetaData$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PubgmMatchEffects.prototype, "loadRoomData$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PubgmMatchEffects.prototype, "loadMatchMetaDataOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PubgmMatchEffects.prototype, "loadRoomDataOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgmMatchEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.PubgMobileMatchResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadMatchMetaData$: [], loadRoomData$: [], loadMatchMetaDataOnce$: [], loadRoomDataOnce$: [] }); })();

class PubgmModule {
}
PubgmModule.ɵfac = function PubgmModule_Factory(t) { return new (t || PubgmModule)(); };
PubgmModule.ɵmod = i0.ɵɵdefineNgModule({ type: PubgmModule });
PubgmModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([PubgmMatchEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PubgmModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgmModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([PubgmMatchEffects])],
            }]
    }], null, null); })();

const FortniteActionTypes = {
    LOAD_MATCH_METADATA: type("[Fortnite] Match Metadata Load"),
    LOAD_ONCE_MATCH_METADATA: loadOnceActionType("[Fortnite] Match Metadata Load Once"),
    LOAD_MATCH_METADATA_COMPLETE: type("[Fortnite] Match Metadata Load Complete"),
    LOAD_LEAGUE_SETTINGS: type("[Fortnite] League Settings Load"),
    LOAD_ONCE_LEAGUE_SETTINGS: loadOnceActionType("[Fortnite] League Settings Load Once"),
    LOAD_LEAGUE_SETTINGS_COMPLETE: type("[Fortnite] League Settings Load Complete"),
    LOAD_LEAGUE_METADATA: type("[Fortnite] League Metadata Load"),
    LOAD_ONCE_LEAGUE_METADATA: loadOnceActionType("[Fortnite] League Metadata Load Once"),
    LOAD_LEAGUE_METADATA_COMPLETE: type("[Fortnite] League Metadata Load Complete"),
};
// Match
class LoadMatchMetaDataAction {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = FortniteActionTypes.LOAD_MATCH_METADATA;
        this.payload = null;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchMetaDataOnceAction extends LoadMatchMetaDataAction {
    constructor() {
        super(...arguments);
        this.type = FortniteActionTypes.LOAD_ONCE_MATCH_METADATA;
    }
}
class LoadMatchMetaDataCompleteAction {
    constructor(matchId, payload, error) {
        this.matchId = matchId;
        this.payload = payload;
        this.error = error;
        this.type = FortniteActionTypes.LOAD_MATCH_METADATA_COMPLETE;
    }
    get id() {
        return this.matchId;
    }
}

/**
 * Workaround since Rootstate needs to be imported
 * @see https://github.com/Microsoft/TypeScript/issues/9944
 */
const getGiState$1 = getGameintegrationState;
function getFortniteState(state) {
    return state.pubg;
}
// Match
function getMatchMetaDataEntity(state) {
    return state.matchMetaData;
}
const getMatchMetaData = createLoadSelector(compose(getMatchMetaDataEntity, getFortniteState, getGiState$1));

const reducers$g = combineReducers({
    matchMetaData: createMultipleLoadReducer(FortniteActionTypes.LOAD_MATCH_METADATA, FortniteActionTypes.LOAD_MATCH_METADATA_COMPLETE),
    leagueMetaData: createMultipleLoadReducer(FortniteActionTypes.LOAD_LEAGUE_METADATA, FortniteActionTypes.LOAD_LEAGUE_METADATA_COMPLETE),
    leagueSettings: createMultipleLoadReducer(FortniteActionTypes.LOAD_LEAGUE_SETTINGS, FortniteActionTypes.LOAD_LEAGUE_SETTINGS_COMPLETE),
});
function fortniteReducer(state, action) {
    return reducers$g(state, action);
}

class FortniteMatchEffects {
    constructor(fortniteMatchResource, actions$, state$) {
        this.fortniteMatchResource = fortniteMatchResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadMatchMetaData$ = this.actions$.pipe(ofType(FortniteActionTypes.LOAD_MATCH_METADATA), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(FortniteActionTypes.LOAD_MATCH_METADATA), filter((action2) => action.matchId === action2.matchId), skip(1));
            return this.fortniteMatchResource.getMetaDataOfMatch(action.matchId).pipe(takeUntil(nextSearch$), map(metaData => new LoadMatchMetaDataCompleteAction(action.matchId, metaData)), catchError(error => of(new LoadMatchMetaDataCompleteAction(action.matchId, null, createErrorObject(error)))));
        }));
        this.loadMatchMetaDataOnce$ = this.actions$.pipe(ofType(FortniteActionTypes.LOAD_ONCE_MATCH_METADATA), mergeMap((action) => {
            return this.state$.select(getMatchMetaData(action.matchId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadMatchMetaDataAction(action.matchId)]));
    }
}
FortniteMatchEffects.ɵfac = function FortniteMatchEffects_Factory(t) { return new (t || FortniteMatchEffects)(i0.ɵɵinject(i1.FortniteMatchResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
FortniteMatchEffects.ɵprov = i0.ɵɵdefineInjectable({ token: FortniteMatchEffects, factory: FortniteMatchEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], FortniteMatchEffects.prototype, "loadMatchMetaData$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], FortniteMatchEffects.prototype, "loadMatchMetaDataOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FortniteMatchEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.FortniteMatchResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadMatchMetaData$: [], loadMatchMetaDataOnce$: [] }); })();

class FortniteModule {
}
FortniteModule.ɵfac = function FortniteModule_Factory(t) { return new (t || FortniteModule)(); };
FortniteModule.ɵmod = i0.ɵɵdefineNgModule({ type: FortniteModule });
FortniteModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([FortniteMatchEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(FortniteModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FortniteModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([FortniteMatchEffects])],
            }]
    }], null, null); })();

const CocActionTypes = {
    LOAD_CONTESTANT_CLAN: type("[Coc] Contestant Clan Load"),
    LOAD_ONCE_CONTESTANT_CLAN: loadOnceActionType("[Coc] Contestant Clan Load Once"),
    LOAD_CONTESTANT_CLAN_COMPLETE: type("[Pubg] Contestant Clan Load Complete"),
};
class LoadContestantClanAction {
    constructor(leagueId, contestantId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.contestantId = contestantId;
        this.ignoreCache = ignoreCache;
        this.type = CocActionTypes.LOAD_CONTESTANT_CLAN;
        this.payload = null;
    }
    get id() {
        return this.leagueId;
    }
    get id2() {
        return this.contestantId;
    }
}
class LoadContestantClanOnceAction extends LoadContestantClanAction {
    constructor() {
        super(...arguments);
        this.type = CocActionTypes.LOAD_ONCE_CONTESTANT_CLAN;
    }
}
class LoadContestantClanCompleteAction {
    constructor(leagueId, contestantId, payload, error) {
        this.leagueId = leagueId;
        this.contestantId = contestantId;
        this.payload = payload;
        this.error = error;
        this.type = CocActionTypes.LOAD_CONTESTANT_CLAN_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
    get id2() {
        return this.contestantId;
    }
}

/**
 * Workaround since Rootstate needs to be imported
 * @see https://github.com/Microsoft/TypeScript/issues/9944
 */
const getGiState = getGameintegrationState;
function getCocState(state) {
    return state.coc;
}
function getContestantClanEntity(state) {
    return state.contestantClan;
}
function getContestantClansForLeague(leagueId) {
    return (state) => state.get(leagueId);
}
function getContestantClansForContestantId(contestantId) {
    return (state) => (state ? state.get(contestantId) : null);
}
const getContestantClan = (leagueId, contestantId) => {
    return compose(getContestantClansForContestantId(contestantId), getContestantClansForLeague(leagueId), getContestantClanEntity, getCocState, getGiState);
};

const reducers$f = combineReducers({
    contestantClan: createNestedMultipleLoadReducer(CocActionTypes.LOAD_CONTESTANT_CLAN, CocActionTypes.LOAD_CONTESTANT_CLAN_COMPLETE),
});
function cocReducer(state, action) {
    return reducers$f(state, action);
}

class CocContestantEffects {
    constructor(cocContestantResource, actions$, state$) {
        this.cocContestantResource = cocContestantResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadConstantClan$ = this.actions$.pipe(ofType(CocActionTypes.LOAD_CONTESTANT_CLAN), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(CocActionTypes.LOAD_CONTESTANT_CLAN), filter((action2) => action.leagueId === action2.leagueId && action.contestantId === action2.contestantId), skip(1));
            return this.cocContestantResource.getClanOfContestant(action.leagueId, action.contestantId).pipe(takeUntil(nextSearch$), map(contestantClan => new LoadContestantClanCompleteAction(action.leagueId, action.contestantId, contestantClan)), catchError(error => {
                if (error.errorCode && error.errorCode === 404) {
                    return of(new LoadContestantClanCompleteAction(action.leagueId, action.contestantId, null));
                }
                return of(new LoadContestantClanCompleteAction(action.leagueId, action.contestantId, null, createErrorObject(error)));
            }));
        }));
        this.loadConstantClanOnce$ = this.actions$.pipe(ofType(CocActionTypes.LOAD_ONCE_CONTESTANT_CLAN), mergeMap((action) => {
            return this.state$.select(getContestantClan(action.leagueId, action.contestantId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadContestantClanAction(action.leagueId, action.contestantId)]));
    }
}
CocContestantEffects.ɵfac = function CocContestantEffects_Factory(t) { return new (t || CocContestantEffects)(i0.ɵɵinject(i1.CocContestantResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
CocContestantEffects.ɵprov = i0.ɵɵdefineInjectable({ token: CocContestantEffects, factory: CocContestantEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], CocContestantEffects.prototype, "loadConstantClan$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], CocContestantEffects.prototype, "loadConstantClanOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CocContestantEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.CocContestantResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadConstantClan$: [], loadConstantClanOnce$: [] }); })();

class CocModule {
}
CocModule.ɵfac = function CocModule_Factory(t) { return new (t || CocModule)(); };
CocModule.ɵmod = i0.ɵɵdefineNgModule({ type: CocModule });
CocModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([CocContestantEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CocModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CocModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([CocContestantEffects])],
            }]
    }], null, null); })();

const AutochessActionTypes = {
    LOAD_AUTOCHESS_MATCH: type("[Autochess] Load autochess match"),
    LOAD_AUTOCHESS_MATCH_COMPLETE: type("[Autochess] Load autochess match complete"),
};
class LoadAutochessMatchAction {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = AutochessActionTypes.LOAD_AUTOCHESS_MATCH;
    }
}
class LoadAutochessMatchCompleteAction {
    constructor(payload, error) {
        this.payload = payload;
        this.error = error;
        this.type = AutochessActionTypes.LOAD_AUTOCHESS_MATCH_COMPLETE;
    }
}

function getAutochessState(state) {
    return state.autochess;
}
function getAutochessMatchEntry(state) {
    return state.autochessMatch;
}
const getAutochessMatch = compose(getAutochessMatchEntry, getAutochessState, getGameintegrationState);

const reducers$e = combineReducers({
    autochessMatch: createLoadReducer(AutochessActionTypes.LOAD_AUTOCHESS_MATCH, AutochessActionTypes.LOAD_AUTOCHESS_MATCH_COMPLETE),
});
function autochessReducer(state, action) {
    return reducers$e(state, action);
}

class AutochessEffects {
    constructor(actions$, autochessResource) {
        this.actions$ = actions$;
        this.autochessResource = autochessResource;
        this.loadAutochessMatch$ = this.actions$.pipe(ofType(AutochessActionTypes.LOAD_AUTOCHESS_MATCH), switchMap((action) => {
            return this.autochessResource.getAutochessMatch(action.matchId).pipe(map(autochessMatch => new LoadAutochessMatchCompleteAction(autochessMatch)), catchError(error => of(new LoadAutochessMatchCompleteAction(null, createErrorObject(error)))));
        }));
    }
}
AutochessEffects.ɵfac = function AutochessEffects_Factory(t) { return new (t || AutochessEffects)(i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i1.AutochessResource)); };
AutochessEffects.ɵprov = i0.ɵɵdefineInjectable({ token: AutochessEffects, factory: AutochessEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], AutochessEffects.prototype, "loadAutochessMatch$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AutochessEffects, [{
        type: Injectable
    }], function () { return [{ type: i2.Actions }, { type: i1.AutochessResource }]; }, { loadAutochessMatch$: [] }); })();

class AutochessModule {
}
AutochessModule.ɵfac = function AutochessModule_Factory(t) { return new (t || AutochessModule)(); };
AutochessModule.ɵmod = i0.ɵɵdefineNgModule({ type: AutochessModule });
AutochessModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([AutochessEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(AutochessModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AutochessModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([AutochessEffects])],
            }]
    }], null, null); })();

const reducers$d = combineReducers({
    pubg: pubgReducer,
    pubgm: pubgmReducer,
    coc: cocReducer,
    autochess: autochessReducer,
});
function gameintegrationReducer(state, action) {
    return reducers$d(state, action);
}

class GameintegrationModule {
}
GameintegrationModule.ɵfac = function GameintegrationModule_Factory(t) { return new (t || GameintegrationModule)(); };
GameintegrationModule.ɵmod = i0.ɵɵdefineNgModule({ type: GameintegrationModule });
GameintegrationModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, PubgModule, PubgmModule, CocModule, AutochessModule, FortniteModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(GameintegrationModule, { imports: [CommonModule, PubgModule, PubgmModule, CocModule, AutochessModule, FortniteModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameintegrationModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, PubgModule, PubgmModule, CocModule, AutochessModule, FortniteModule],
            }]
    }], null, null); })();

const PlayerActionTypes = {
    SELECT_PLAYER: type("[Player] Select"),
    LOAD_PLAYER: type("[Player] Load"),
    LOAD_ONCE_PLAYER: loadOnceActionType("[Player] Load Once"),
    LOAD_MULTIPLE_PLAYERS: type("[Player] Multiple Load"),
    LOAD_ONCE_MULTIPLE_PLAYERS: loadOnceActionType("[Player] Multiple Load Once"),
    LOAD_PLAYER_COMPLETE: type("[Player] Load Complete"),
    LOAD_MULTIPLE_PLAYERS_COMPLETE: type("[Player] Multiple Load Complete"),
    // Description
    LOAD_PLAYER_SHORT_DESCRIPTION: type("[Player] Load Player Short Description"),
    LOAD_ONCE_PLAYER_SHORT_DESCRIPTION: type("[Player] Load Once Player Short Description"),
    LOAD_PLAYER_SHORT_DESCRIPTION_COMPLETE: type("[Player] Load Player Short Description Complete"),
    // Matches
    LOAD_MATCHES: type("[Player] Load Player Matches"),
    LOAD_ONCE_MATCHES: loadOnceActionType("[Player] Load Player Matches Once"),
    LOAD_MATCHES_COMPLETE: type("[Player] Load Player Matches Complete"),
    // Matches
    LOAD_LEAGUES: type("[Player] Load Player Leagues"),
    LOAD_ONCE_LEAGUES: loadOnceActionType("[Player] Load Player Leagues Once"),
    LOAD_LEAGUES_COMPLETE: type("[Player] Load Player Leagues Complete"),
};
class SelectPlayerAction {
    constructor(playerId) {
        this.playerId = playerId;
        this.type = PlayerActionTypes.SELECT_PLAYER;
        this.payload = null;
    }
}
class LoadPlayerAction {
    constructor(playerId, ignoreCache = false) {
        this.playerId = playerId;
        this.ignoreCache = ignoreCache;
        this.type = PlayerActionTypes.LOAD_PLAYER;
        this.payload = null;
    }
    get id() {
        return this.playerId;
    }
}
class LoadPlayerOnceAction extends LoadPlayerAction {
    constructor() {
        super(...arguments);
        this.type = PlayerActionTypes.LOAD_ONCE_PLAYER;
    }
}
class LoadPlayersAction {
    constructor(playerIds, ignoreCache = false) {
        this.playerIds = playerIds;
        this.ignoreCache = ignoreCache;
        this.type = PlayerActionTypes.LOAD_MULTIPLE_PLAYERS;
        this.payload = null;
    }
}
class LoadPlayersCompleteAction {
    constructor(players) {
        this.players = players;
        this.type = PlayerActionTypes.LOAD_MULTIPLE_PLAYERS_COMPLETE;
        this.payload = null;
    }
}
class LoadPlayersOnceAction extends LoadPlayersAction {
    constructor() {
        super(...arguments);
        this.type = PlayerActionTypes.LOAD_ONCE_MULTIPLE_PLAYERS;
    }
}
class LoadPlayerCompleteAction {
    constructor(playerId, payload, error) {
        this.playerId = playerId;
        this.payload = payload;
        this.error = error;
        this.type = PlayerActionTypes.LOAD_PLAYER_COMPLETE;
    }
    get id() {
        return this.playerId;
    }
}
// Description
class LoadPlayerShortDescriptionAction {
    constructor(playerId, ignoreCache = false) {
        this.playerId = playerId;
        this.ignoreCache = ignoreCache;
        this.type = PlayerActionTypes.LOAD_PLAYER_SHORT_DESCRIPTION;
        this.payload = null;
    }
    get id() {
        return this.playerId;
    }
}
class LoadPlayerShortDescriptionOnceAction extends LoadPlayerShortDescriptionAction {
    constructor() {
        super(...arguments);
        this.type = PlayerActionTypes.LOAD_ONCE_PLAYER_SHORT_DESCRIPTION;
    }
}
class LoadPlayerShortDescriptionCompleteAction {
    constructor(playerId, payload, error) {
        this.playerId = playerId;
        this.payload = payload;
        this.error = error;
        this.type = PlayerActionTypes.LOAD_PLAYER_SHORT_DESCRIPTION_COMPLETE;
    }
    get id() {
        return this.playerId;
    }
}
// Matches
class LoadPlayerMatchesAction {
    constructor(playerId, matchesQuery, ignoreCache = false) {
        this.playerId = playerId;
        this.matchesQuery = matchesQuery;
        this.ignoreCache = ignoreCache;
        this.type = PlayerActionTypes.LOAD_MATCHES;
        this.payload = null;
    }
    get id() {
        return this.playerId;
    }
    get query() {
        return this.matchesQuery;
    }
}
class LoadPlayerMatchesOnceAction extends LoadPlayerMatchesAction {
    constructor() {
        super(...arguments);
        this.type = PlayerActionTypes.LOAD_ONCE_MATCHES;
    }
}
class LoadPlayerMatchesCompleteAction {
    constructor(playerId, payload, error) {
        this.playerId = playerId;
        this.payload = payload;
        this.error = error;
        this.type = PlayerActionTypes.LOAD_MATCHES_COMPLETE;
    }
    get id() {
        return this.playerId;
    }
}
// Leagues
class LoadPlayerLeaguesAction {
    constructor(playerId, ignoreCache = false) {
        this.playerId = playerId;
        this.ignoreCache = ignoreCache;
        this.type = PlayerActionTypes.LOAD_LEAGUES;
        this.payload = null;
    }
    get id() {
        return this.playerId;
    }
}
class LoadPlayerLeaguesOnceAction extends LoadPlayerLeaguesAction {
    constructor() {
        super(...arguments);
        this.type = PlayerActionTypes.LOAD_ONCE_LEAGUES;
    }
}
class LoadPlayerLeaguesCompleteAction {
    constructor(playerId, payload, error) {
        this.playerId = playerId;
        this.payload = payload;
        this.error = error;
        this.type = PlayerActionTypes.LOAD_LEAGUES_COMPLETE;
    }
    get id() {
        return this.playerId;
    }
}

const loadPlayerReducer = createMultipleLoadReducer(PlayerActionTypes.LOAD_PLAYER, PlayerActionTypes.LOAD_PLAYER_COMPLETE);
const reducers$c = combineReducers({
    players: queueReducers([
        loadPlayerReducer,
        (state, action) => {
            if (action.type === PlayerActionTypes.LOAD_MULTIPLE_PLAYERS) {
                const newAction = action;
                newAction.playerIds.forEach(playerId => {
                    state = loadPlayerReducer(state, new LoadPlayerAction(playerId));
                });
            }
            if (action.type === PlayerActionTypes.LOAD_MULTIPLE_PLAYERS_COMPLETE) {
                const newAction = action;
                newAction.players.forEach(player => {
                    state = loadPlayerReducer(state, new LoadPlayerCompleteAction(+player.id, player, null));
                });
            }
            return state;
        },
    ]),
    matches: createMultipleLoadReducer(PlayerActionTypes.LOAD_MATCHES, PlayerActionTypes.LOAD_MATCHES_COMPLETE),
    leagues: createMultipleLoadReducer(PlayerActionTypes.LOAD_LEAGUES, PlayerActionTypes.LOAD_LEAGUES_COMPLETE),
    shortDescription: createMultipleLoadReducer(PlayerActionTypes.LOAD_PLAYER_SHORT_DESCRIPTION, PlayerActionTypes.LOAD_PLAYER_SHORT_DESCRIPTION_COMPLETE),
});
function playerReducer(state, action) {
    return reducers$c(state, action);
}

const LeagueActionTypes = {
    SELECT_LEAGUE: type("[League] Select"),
    LOAD_LEAGUE: type("[League] Load"),
    LOAD_ONCE_LEAGUE: loadOnceActionType("[League] Load Once"),
    LOAD_MULTIPLE_LEAGUES: type("[League] Multiple Load"),
    LOAD_ONCE_MULTIPLE_LEAGUES: loadOnceActionType("[League] Multiple Load Once"),
    LOAD_LEAGUE_COMPLETE: type("[League] Load Complete"),
    LOAD_MULTIPLE_LEAGUES_COMPLETE: type("[League] Multiple Load Complete"),
    LOAD_PARTICIPANTS: type("[League] Load Participants"),
    LOAD_ONCE_PARTICIPANTS: loadOnceActionType("[League] Load Participants Once"),
    LOAD_PARTICIPANTS_COMPLETE: type("[League] Load Participants Complete"),
    LOAD_LEAGUE_RESTRICTIONS: type("[League] Load Restrictions"),
    LOAD_ONCE_LEAGUE_RESTRICTIONS: loadOnceActionType("[League] Load Restrictions Once"),
    LOAD_LEAGUE_RESTRICTIONS_COMPLETE: type("[League] Load Restrictions Complete"),
    LOAD_MATCHES: type("[League] Load Matches"),
    LOAD_ONCE_MATCHES: loadOnceActionType("[League] Load Matches Once"),
    LOAD_MATCHES_COMPLETE: type("[League] Load Matches Complete"),
    LOAD_RESULTS: type("[League] Load Results"),
    LOAD_ONCE_RESULTS: loadOnceActionType("[League] Load Results Once"),
    LOAD_RESULTS_COMPLETE: type("[League] Load Results Complete"),
    LEAVE_LEAGUE: type("[League] Leave"),
};
class SelectLeagueAction {
    constructor(leagueId) {
        this.leagueId = leagueId;
        this.type = LeagueActionTypes.SELECT_LEAGUE;
        this.payload = null;
    }
}
class LoadLeagueAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = LeagueActionTypes.LOAD_LEAGUE;
        this.payload = null;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadLeagueOnceAction extends LoadLeagueAction {
    constructor() {
        super(...arguments);
        this.type = LeagueActionTypes.LOAD_ONCE_LEAGUE;
    }
}
class LoadLeaguesAction {
    constructor(leagueIds, ignoreCache = false) {
        this.leagueIds = leagueIds;
        this.ignoreCache = ignoreCache;
        this.type = LeagueActionTypes.LOAD_MULTIPLE_LEAGUES;
        this.payload = null;
    }
}
class LoadLeaguesCompleteAction {
    constructor(leagues) {
        this.leagues = leagues;
        this.type = LeagueActionTypes.LOAD_MULTIPLE_LEAGUES_COMPLETE;
        this.payload = null;
    }
}
class LoadLeaguesOnceAction extends LoadLeaguesAction {
    constructor() {
        super(...arguments);
        this.type = LeagueActionTypes.LOAD_ONCE_MULTIPLE_LEAGUES;
    }
}
class LoadLeagueCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = LeagueActionTypes.LOAD_LEAGUE_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
// Participants
class LoadParticipantsAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = LeagueActionTypes.LOAD_PARTICIPANTS;
        this.payload = null;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadParticipantsOnceAction extends LoadParticipantsAction {
    constructor() {
        super(...arguments);
        this.type = LeagueActionTypes.LOAD_ONCE_PARTICIPANTS;
    }
}
class LoadParticipantsCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = LeagueActionTypes.LOAD_PARTICIPANTS_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
// Restrictions
class LoadLeagueRestrictionsAction {
    constructor(leagueId, contestantId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.contestantId = contestantId;
        this.ignoreCache = ignoreCache;
        this.type = LeagueActionTypes.LOAD_LEAGUE_RESTRICTIONS;
        this.payload = null;
    }
    get id() {
        return this.leagueId;
    }
    get id2() {
        return this.contestantId;
    }
}
class LoadLeagueRestrictionsOnceAction extends LoadLeagueRestrictionsAction {
    constructor() {
        super(...arguments);
        this.type = LeagueActionTypes.LOAD_ONCE_LEAGUE_RESTRICTIONS;
    }
}
class LoadLeagueRestrictionsCompleteAction {
    constructor(leagueId, contestantId, payload, error) {
        this.leagueId = leagueId;
        this.contestantId = contestantId;
        this.payload = payload;
        this.error = error;
        this.type = LeagueActionTypes.LOAD_LEAGUE_RESTRICTIONS_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
    get id2() {
        return this.contestantId;
    }
}
// Contestant actions
class LeaveLeagueAction {
    constructor(leagueId, contestantId) {
        this.leagueId = leagueId;
        this.contestantId = contestantId;
        this.payload = null;
        this.type = LeagueActionTypes.LEAVE_LEAGUE;
    }
}
// Matches
class LoadLeagueMatchesAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = LeagueActionTypes.LOAD_MATCHES;
        this.payload = null;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadLeagueMatchesOnceAction extends LoadLeagueMatchesAction {
    constructor() {
        super(...arguments);
        this.type = LeagueActionTypes.LOAD_ONCE_MATCHES;
    }
}
class LoadLeagueMatchesCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = LeagueActionTypes.LOAD_MATCHES_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
// Results
class LoadLeagueResultsAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = LeagueActionTypes.LOAD_RESULTS;
        this.payload = null;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadLeagueResultsOnceAction extends LoadLeagueResultsAction {
    constructor() {
        super(...arguments);
        this.type = LeagueActionTypes.LOAD_ONCE_RESULTS;
    }
}
class LoadLeagueResultsCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = LeagueActionTypes.LOAD_RESULTS_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}

const MatchActionTypes = {
    LOAD_MATCH: type("[Match] Load"),
    LOAD_ONCE_MATCH: loadOnceActionType("[Match] Load Once"),
    LOAD_MULTIPLE_MATCHES: type("[Match] Multiple Load"),
    LOAD_MATCH_COMPLETE: type("[Match] Load Complete"),
    LOAD_MEDIA: type("[Match] Media Load"),
    LOAD_ONCE_MEDIA: loadOnceActionType("[Match] Media Load Once"),
    LOAD_MEDIA_COMPLETE: type("[Match] Media Load Complete"),
};
class LoadMatchAction {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = MatchActionTypes.LOAD_MATCH;
        this.payload = null;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchOnceAction extends LoadMatchAction {
    constructor() {
        super(...arguments);
        this.type = MatchActionTypes.LOAD_ONCE_MATCH;
    }
}
class LoadMatchesAction {
    constructor(matchIds, ignoreCache = false) {
        this.matchIds = matchIds;
        this.ignoreCache = ignoreCache;
        this.type = MatchActionTypes.LOAD_MULTIPLE_MATCHES;
        this.payload = null;
    }
}
class LoadMatchCompleteAction {
    constructor(matchId, payload, error) {
        this.matchId = matchId;
        this.payload = payload;
        this.error = error;
        this.type = MatchActionTypes.LOAD_MATCH_COMPLETE;
    }
    get id() {
        return this.matchId;
    }
}
// Match Media
class LoadMediaAction {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = MatchActionTypes.LOAD_MEDIA;
        this.payload = null;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMediaOnceAction extends LoadMediaAction {
    constructor() {
        super(...arguments);
        this.type = MatchActionTypes.LOAD_ONCE_MEDIA;
    }
}
class LoadMediaCompleteAction {
    constructor(matchId, payload, error) {
        this.matchId = matchId;
        this.payload = payload;
        this.error = error;
        this.type = MatchActionTypes.LOAD_MEDIA_COMPLETE;
    }
    get id() {
        return this.matchId;
    }
}

const ContestantActionTypes = {
    LOAD_CONTESTANTS: type("[Contestants] Load"),
    LOAD_ONCE_CONTESTANTS: loadOnceActionType("[Contestants] Load Once"),
    LOAD_CONTESTANTS_COMPLETE: type("[Contestants] Load Complete"),
};
class LoadContestantsAction {
    constructor(leagueId, userId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.userId = userId;
        this.ignoreCache = ignoreCache;
        this.type = ContestantActionTypes.LOAD_CONTESTANTS;
        this.payload = null;
    }
    get id() {
        return this.leagueId;
    }
    get id2() {
        return this.userId;
    }
}
class LoadContestantsOnceAction extends LoadContestantsAction {
    constructor() {
        super(...arguments);
        this.type = ContestantActionTypes.LOAD_ONCE_CONTESTANTS;
    }
}
class LoadContestantsCompleteAction {
    constructor(leagueId, userId, payload, error) {
        this.leagueId = leagueId;
        this.userId = userId;
        this.payload = payload;
        this.error = error;
        this.type = ContestantActionTypes.LOAD_CONTESTANTS_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
    get id2() {
        return this.userId;
    }
}

/**
 * @param leagueId
 * @param contestant
 */
function normalizeContestant(leagueId, contestant) {
    const actions = [];
    const teamEntity = new schema.Entity("team");
    const userEntity = new schema.Entity("user");
    const contestantEntity = new schema.Entity("contestant", {
        team: teamEntity,
        user: userEntity,
    });
    const data = normalize(contestant, contestantEntity);
    const players = map$1(data.entities.user, user => user);
    if (players && players.length) {
        actions.push(new LoadPlayersCompleteAction(players));
    }
    const teams = map$1(data.entities.team, team => team);
    if (teams && teams.length) {
        actions.push(new LoadTeamsCompleteAction(teams));
    }
    return actions;
}

const reducers$b = combineReducers({
    contestants: createNestedMultipleLoadReducer(ContestantActionTypes.LOAD_CONTESTANTS, ContestantActionTypes.LOAD_CONTESTANTS_COMPLETE),
});
function contestantReducer(state, action) {
    return reducers$b(state, action);
}

function getContestantState(state) {
    return state.contestant;
}
function getContestantEntities(state) {
    return state.contestants;
}
function getContestantsForLeague(leagueId) {
    return (state) => state.get(leagueId);
}
function getContestantInfoForUser(userId) {
    return (state) => {
        if (!state) {
            return null;
        }
        return state.get(userId);
    };
}
function getContestantInfo(contestantId) {
    return (state) => {
        if (!state || state.error || !state.entry) {
            return null;
        }
        return find(state.entry, contestant => +contestant.id === contestantId);
    };
}
const getContestants = (leagueId, userId) => {
    return compose(getContestantInfoForUser(userId), getContestantsForLeague(leagueId), getContestantEntities, getContestantState);
};
const getContestant = (leagueId, userId, contestantId) => {
    return compose(getContestantInfo(contestantId), getContestantInfoForUser(userId), getContestantsForLeague(leagueId), getContestantEntities, getContestantState);
};

class ContestantEffects {
    constructor(playerResource, actions$, state$) {
        this.playerResource = playerResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadContestants$ = this.actions$.pipe(ofType(ContestantActionTypes.LOAD_CONTESTANTS), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(ContestantActionTypes.LOAD_CONTESTANTS), filter((action2) => {
                return action.leagueId === action2.leagueId && action.userId === action2.userId;
            }), skip(1));
            return this.playerResource.getContestantsOfPlayer(action.userId, action.leagueId).pipe(takeUntil(nextSearch$), map(contestants => {
                return new LoadContestantsCompleteAction(action.leagueId, action.userId, contestants);
            }), catchError(error => of(new LoadContestantsCompleteAction(action.leagueId, action.userId, null, createErrorObject(error)))));
        }));
        this.loadContestantsOnce$ = this.actions$.pipe(ofType(ContestantActionTypes.LOAD_ONCE_CONTESTANTS), mergeMap((action) => {
            return this.state$.select(getContestants(action.leagueId, action.userId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadContestantsAction(action.leagueId, action.userId)]));
    }
}
ContestantEffects.ɵfac = function ContestantEffects_Factory(t) { return new (t || ContestantEffects)(i0.ɵɵinject(i1.PlayerResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
ContestantEffects.ɵprov = i0.ɵɵdefineInjectable({ token: ContestantEffects, factory: ContestantEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], ContestantEffects.prototype, "loadContestants$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], ContestantEffects.prototype, "loadContestantsOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ContestantEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.PlayerResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadContestants$: [], loadContestantsOnce$: [] }); })();

class ContestantModule {
}
ContestantModule.ɵfac = function ContestantModule_Factory(t) { return new (t || ContestantModule)(); };
ContestantModule.ɵmod = i0.ɵɵdefineNgModule({ type: ContestantModule });
ContestantModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([ContestantEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(ContestantModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ContestantModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([ContestantEffects])],
            }]
    }], null, null); })();

/**
 * @param playerId
 * @param matches
 */
function normalizePlayerMatches(playerId, matches) {
    const actions = [new LoadPlayerMatchesCompleteAction(playerId, matches.map(match => match.id))];
    forEach(matches, match => {
        actions.push(new LoadMatchCompleteAction(match.id, match));
    });
    return actions;
}
/**
 * @param playerId
 * @param playerLeagues
 */
function normalizePlayerLeagues(playerId, playerLeagues) {
    const actions = [new LoadPlayerLeaguesCompleteAction(playerId, playerLeagues.map(x => x.league.id))];
    forEach(playerLeagues, (playerLeague) => {
        actions.push(new LoadContestantsCompleteAction(playerLeague.league.id, playerId, [playerLeague.contestant]));
        actions.push(new LoadLeagueCompleteAction(playerLeague.league.id, playerLeague.league));
    });
    return actions;
}

function getPlayerEntity(state) {
    return state.players;
}
function getPlayerState(state) {
    return state.player;
}
const getPlayer = createLoadSelector(compose(getPlayerEntity, getPlayerState));
const getPlayers = createLoadManySelector(compose(getPlayerEntity, getPlayerState));
function getShortDescriptionEntity(state) {
    return state.shortDescription;
}
const getShortDescription = createLoadSelector(compose(getShortDescriptionEntity, getPlayerState));

class PlayerEffects {
    constructor(playerResource, actions$, state$) {
        this.playerResource = playerResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadPlayer$ = this.actions$.pipe(ofType(PlayerActionTypes.LOAD_PLAYER), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(PlayerActionTypes.LOAD_PLAYER), filter((action2) => action.playerId === action2.playerId), skip(1));
            return this.playerResource.getPlayer(action.playerId).pipe(takeUntil(nextSearch$), map(playerR => new LoadPlayerCompleteAction(action.playerId, playerR)), catchError(error => of(new LoadPlayerCompleteAction(action.playerId, null, createErrorObject(error)))));
        }));
        this.loadPlayers$ = this.actions$.pipe(ofType(PlayerActionTypes.LOAD_MULTIPLE_PLAYERS), mergeMap((action) => {
            if (!action.playerIds.length) {
                return [];
            }
            return this.playerResource.getPlayers(action.playerIds).pipe(first(), map(players => new LoadPlayersCompleteAction(players)), catchError(error => {
                return action.playerIds.map(id => new LoadPlayerCompleteAction(id, null, createErrorObject(error)));
            }));
        }));
        this.loadPlayersOnce$ = this.actions$.pipe(ofType(PlayerActionTypes.LOAD_ONCE_MULTIPLE_PLAYERS), mergeMap((action) => {
            if (!action.playerIds.length) {
                return [];
            }
            return combineLatest(action.playerIds.map(id => {
                return this.state$.select(getPlayer(id)).pipe(first(), map(player => (exists(player) ? undefined : id)));
            })).pipe(map(playerIds => without(playerIds, undefined)), first());
        }), filter(playerIds => Boolean(playerIds.length)), map(playerIds => new LoadPlayersAction(playerIds)));
        this.selectPlayer$ = this.actions$.pipe(ofType(PlayerActionTypes.SELECT_PLAYER), mergeMap((action) => {
            return [new LoadPlayerAction(action.playerId), new LoadPlayerMatchesAction(action.playerId)];
        }));
        this.loadPlayerOnce$ = this.actions$.pipe(ofType(PlayerActionTypes.LOAD_ONCE_PLAYER), mergeMap((action) => {
            return this.state$.select(getPlayer(action.playerId)).pipe(first(), filter(data => !exists(data)), map(player => action));
        }), mergeMap(action => [new LoadPlayerAction(action.playerId)]));
        this.loadShortDescription$ = this.actions$.pipe(ofType(PlayerActionTypes.LOAD_PLAYER_SHORT_DESCRIPTION), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(PlayerActionTypes.LOAD_PLAYER_SHORT_DESCRIPTION), filter((action2) => action.playerId === action2.playerId), skip(1));
            return this.playerResource.getShortDescriptionOfPlayer(action.playerId).pipe(takeUntil(nextSearch$), map(shortDesc => new LoadPlayerShortDescriptionCompleteAction(action.playerId, shortDesc)), catchError(error => of(new LoadPlayerShortDescriptionCompleteAction(action.playerId, null, createErrorObject(error)))));
        }));
        this.loadShortDescriptionOnce$ = this.actions$.pipe(ofType(PlayerActionTypes.LOAD_ONCE_PLAYER_SHORT_DESCRIPTION), mergeMap((action) => {
            return this.state$.select(getShortDescription(action.playerId)).pipe(first(), filter(data => !exists(data)), map(shortDesc => action));
        }), mergeMap(action => [new LoadPlayerShortDescriptionAction(action.playerId)]));
        this.loadPlayerMatches$ = this.actions$.pipe(ofType(PlayerActionTypes.LOAD_MATCHES), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(PlayerActionTypes.LOAD_MATCHES), filter((action2) => action.playerId === action2.playerId), skip(1));
            return this.playerResource.getMatchesOfPlayer(action.playerId, action.matchesQuery).pipe(takeUntil(nextSearch$), mergeMap(playerMatches => normalizePlayerMatches(action.playerId, playerMatches)), catchError(error => of(new LoadPlayerMatchesCompleteAction(action.playerId, null, createErrorObject(error)))));
        }));
    }
}
PlayerEffects.ɵfac = function PlayerEffects_Factory(t) { return new (t || PlayerEffects)(i0.ɵɵinject(i1.PlayerResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
PlayerEffects.ɵprov = i0.ɵɵdefineInjectable({ token: PlayerEffects, factory: PlayerEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PlayerEffects.prototype, "loadPlayer$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PlayerEffects.prototype, "loadPlayers$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PlayerEffects.prototype, "loadPlayersOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PlayerEffects.prototype, "selectPlayer$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PlayerEffects.prototype, "loadPlayerOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PlayerEffects.prototype, "loadShortDescription$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PlayerEffects.prototype, "loadShortDescriptionOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], PlayerEffects.prototype, "loadPlayerMatches$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PlayerEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.PlayerResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadPlayer$: [], loadPlayers$: [], loadPlayersOnce$: [], selectPlayer$: [], loadPlayerOnce$: [], loadShortDescription$: [], loadShortDescriptionOnce$: [], loadPlayerMatches$: [] }); })();

class PlayerModule {
}
PlayerModule.ɵfac = function PlayerModule_Factory(t) { return new (t || PlayerModule)(); };
PlayerModule.ɵmod = i0.ɵɵdefineNgModule({ type: PlayerModule });
PlayerModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([PlayerEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PlayerModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PlayerModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([PlayerEffects])],
            }]
    }], null, null); })();

const ActionTypes = {
    LOAD_PRIZES: type("[League Namespace] Load prizes"),
    LOAD_PRIZES_COMPLETE: type("[League Namespace] Load prizes complete"),
    LOAD_SPONSOR: type("[League Namespace] Load sponsor"),
    LOAD_SPONSOR_COMPLETE: type("[League Namespace] Load sponsor complete"),
    LOAD_CHAT_INFO: type("[League Namespace] Load chatinfo"),
    LOAD_CHAT_INFO_COMPLETE: type("[League Namespace] Load chatinfo complete"),
    LOAD_QUICK_RULES: type("[League Namespace] Load quickrules"),
    LOAD_QUICK_RULES_COMPLETE: type("[League Namespace] Load quickrules complete"),
    LOAD_HELP: type("[League Namespace] Load help"),
    LOAD_HELP_COMPLETE: type("[League Namespace] Load help complete"),
    LOAD_COVERAGE: type("[League Namespace] Load coverage"),
    LOAD_COVERAGE_COMPLETE: type("[League Namespace] Load coverage complete"),
    LOAD_GAME_RULES: type("[League Namespace] Load gamerules"),
    LOAD_GAME_RULES_COMPLETE: type("[League Namespace] Load gamerules complete"),
    LOAD_SYSTEM_RULES: type("[League Namespace] Load systemrules"),
    LOAD_SYSTEM_RULES_COMPLETE: type("[League Namespace] Load systemrules complete"),
    LOAD_GENERAL_RULES: type("[League Namespace] Load generalrules"),
    LOAD_GENERAL_RULES_COMPLETE: type("[League Namespace] Load generalrules complete"),
};
class LoadPrizesAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = ActionTypes.LOAD_PRIZES;
        this.namespace = LeagueNamespaceTypesEnum.prizes;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadPrizesCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = ActionTypes.LOAD_PRIZES_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadSponsorAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = ActionTypes.LOAD_SPONSOR;
        this.namespace = LeagueNamespaceTypesEnum.sponsor;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadSponsorCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = ActionTypes.LOAD_SPONSOR_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadChatInfoAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = ActionTypes.LOAD_CHAT_INFO;
        this.payload = null;
        this.namespace = LeagueNamespaceTypesEnum.chatinfo;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadChatInfoCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = ActionTypes.LOAD_CHAT_INFO_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadQuickRulesAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = ActionTypes.LOAD_QUICK_RULES;
        this.payload = null;
        this.namespace = LeagueNamespaceTypesEnum.quickrules;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadQuickRulesCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = ActionTypes.LOAD_QUICK_RULES_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadHelpAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = ActionTypes.LOAD_HELP;
        this.payload = null;
        this.namespace = LeagueNamespaceTypesEnum.help;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadHelpCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = ActionTypes.LOAD_HELP_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadCoverageAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = ActionTypes.LOAD_COVERAGE;
        this.payload = null;
        this.namespace = LeagueNamespaceTypesEnum.coverage;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadCoverageCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = ActionTypes.LOAD_COVERAGE_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadGameRulesAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = ActionTypes.LOAD_GAME_RULES;
        this.payload = null;
        this.namespace = LeagueNamespaceTypesEnum.gamerules;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadGameRulesCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = ActionTypes.LOAD_GAME_RULES_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadSystemRulesAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = ActionTypes.LOAD_SYSTEM_RULES;
        this.payload = null;
        this.namespace = LeagueNamespaceTypesEnum.systemrules;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadSystemRulesCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = ActionTypes.LOAD_SYSTEM_RULES_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadGeneralRulesAction {
    constructor(leagueId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.ignoreCache = ignoreCache;
        this.type = ActionTypes.LOAD_GENERAL_RULES;
        this.payload = null;
        this.namespace = LeagueNamespaceTypesEnum.generalrules;
    }
    get id() {
        return this.leagueId;
    }
}
class LoadGeneralRulesCompleteAction {
    constructor(leagueId, payload, error) {
        this.leagueId = leagueId;
        this.payload = payload;
        this.error = error;
        this.type = ActionTypes.LOAD_GENERAL_RULES_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
}

const reducers$a = combineReducers({
    prizes: createMultipleLoadReducer(ActionTypes.LOAD_PRIZES, ActionTypes.LOAD_PRIZES_COMPLETE),
    sponsor: createMultipleLoadReducer(ActionTypes.LOAD_SPONSOR, ActionTypes.LOAD_SPONSOR_COMPLETE),
    chatinfo: createMultipleLoadReducer(ActionTypes.LOAD_CHAT_INFO, ActionTypes.LOAD_CHAT_INFO_COMPLETE),
    quickrules: createMultipleLoadReducer(ActionTypes.LOAD_QUICK_RULES, ActionTypes.LOAD_QUICK_RULES_COMPLETE),
    help: createMultipleLoadReducer(ActionTypes.LOAD_HELP, ActionTypes.LOAD_HELP_COMPLETE),
    coverage: createMultipleLoadReducer(ActionTypes.LOAD_COVERAGE, ActionTypes.LOAD_COVERAGE_COMPLETE),
    gamerules: createMultipleLoadReducer(ActionTypes.LOAD_GAME_RULES, ActionTypes.LOAD_GAME_RULES_COMPLETE),
    systemrules: createMultipleLoadReducer(ActionTypes.LOAD_SYSTEM_RULES, ActionTypes.LOAD_SYSTEM_RULES_COMPLETE),
    generalrules: createMultipleLoadReducer(ActionTypes.LOAD_GENERAL_RULES, ActionTypes.LOAD_GENERAL_RULES_COMPLETE),
});
function leagueNamespaceReducer(state, action) {
    return reducers$a(state, action);
}

function getLeagueState$1(state) {
    return state.league;
}
function getLeagueNamespaceState(state) {
    return state.namespace;
}
function getPrizesEntity(state) {
    return state.prizes;
}
function getSponsorEntity(state) {
    return state.sponsor;
}
function getChatinfoEntity(state) {
    return state.chatinfo;
}
function getQuickRulesEntity(state) {
    return state.quickrules;
}
function getHelpEntity(state) {
    return state.help;
}
function getCoverageEntity(state) {
    return state.coverage;
}
function getGameRulesEntity(state) {
    return state.gamerules;
}
function getSystemRulesEntity(state) {
    return state.systemrules;
}
function getGeneralRulesEntity(state) {
    return state.generalrules;
}
const getPrizes = createLoadSelector(compose(getPrizesEntity, getLeagueNamespaceState, getLeagueState$1));
const getSponsor = createLoadSelector(compose(getSponsorEntity, getLeagueNamespaceState, getLeagueState$1));
const getChatinfo = createLoadSelector(compose(getChatinfoEntity, getLeagueNamespaceState, getLeagueState$1));
const getQuickRules = createLoadSelector(compose(getQuickRulesEntity, getLeagueNamespaceState, getLeagueState$1));
const getHelp = createLoadSelector(compose(getHelpEntity, getLeagueNamespaceState, getLeagueState$1));
const getCoverage = createLoadSelector(compose(getCoverageEntity, getLeagueNamespaceState, getLeagueState$1));
const getGameRules = createLoadSelector(compose(getGameRulesEntity, getLeagueNamespaceState, getLeagueState$1));
const getSystemRules = createLoadSelector(compose(getSystemRulesEntity, getLeagueNamespaceState, getLeagueState$1));
const getGeneralRules = createLoadSelector(compose(getGeneralRulesEntity, getLeagueNamespaceState, getLeagueState$1));

function getPrivateProfile(state) {
    return state.user;
}
function getPremium(state) {
    return state.premium;
}
function getTeams(state) {
    return state.teams;
}
function getTeam(teamId) {
    return (state) => {
        const team = find(state.entry, membership => {
            return teamId === +membership.team.id;
        });
        return team ? team.team : null;
    };
}
function getUserState(state) {
    return state.user;
}
function getShopDiscountCodes(state) {
    return state.shopifyCodes;
}
const getUser = compose(getPrivateProfile, getUserState);
const getUserPremium = compose(getPremium, getUserState);
const getUserTeams = compose(getTeams, getUserState);
const getUserShopDiscountCodes = compose(getShopDiscountCodes, getUserState);
const getUserTeam = (teamId) => compose(getTeam(teamId), getUserTeams);

const ACTION_TYPE_NAMESPACE_MAP = {
    [ActionTypes.LOAD_PRIZES]: LeagueNamespaceTypesEnum.prizes,
    [ActionTypes.LOAD_SPONSOR]: LeagueNamespaceTypesEnum.sponsor,
    [ActionTypes.LOAD_CHAT_INFO]: LeagueNamespaceTypesEnum.chatinfo,
    [ActionTypes.LOAD_QUICK_RULES]: LeagueNamespaceTypesEnum.quickrules,
    [ActionTypes.LOAD_HELP]: LeagueNamespaceTypesEnum.help,
    [ActionTypes.LOAD_COVERAGE]: LeagueNamespaceTypesEnum.coverage,
    [ActionTypes.LOAD_GAME_RULES]: LeagueNamespaceTypesEnum.gamerules,
    [ActionTypes.LOAD_SYSTEM_RULES]: LeagueNamespaceTypesEnum.systemrules,
    [ActionTypes.LOAD_GENERAL_RULES]: LeagueNamespaceTypesEnum.generalrules,
};
class NamespaceEffects {
    constructor(leagueResource, actions$, store) {
        this.leagueResource = leagueResource;
        this.actions$ = actions$;
        this.store = store;
        this.loadNamespace$ = this.actions$.pipe(ofType(ActionTypes.LOAD_PRIZES, ActionTypes.LOAD_SPONSOR, ActionTypes.LOAD_CHAT_INFO, ActionTypes.LOAD_QUICK_RULES, ActionTypes.LOAD_HELP, ActionTypes.LOAD_COVERAGE, ActionTypes.LOAD_GAME_RULES, ActionTypes.LOAD_SYSTEM_RULES, ActionTypes.LOAD_GENERAL_RULES), withLatestFrom(this.store.select(getUser).pipe(map(user => user.entry))), mergeMap(([action, user]) => {
            const namespaceString = ACTION_TYPE_NAMESPACE_MAP[action.type];
            return this.leagueResource.getLeagueNamespace(action.leagueId, namespaceString, user ? user.language : null).pipe(map((namespaceData) => {
                return this.getCompleteAction(action, namespaceData, null);
            }), catchError(error => {
                return of(this.getCompleteAction(action, null, createErrorObject(error)));
            }));
        }));
    }
    getCompleteAction(action, namespaceData, error) {
        let completeAction;
        switch (action.type) {
            case ActionTypes.LOAD_PRIZES:
                completeAction = new LoadPrizesCompleteAction(action.leagueId, namespaceData, error);
                break;
            case ActionTypes.LOAD_SPONSOR:
                completeAction = new LoadSponsorCompleteAction(action.leagueId, namespaceData, error);
                break;
            case ActionTypes.LOAD_CHAT_INFO:
                completeAction = new LoadChatInfoCompleteAction(action.leagueId, namespaceData, error);
                break;
            case ActionTypes.LOAD_QUICK_RULES:
                completeAction = new LoadQuickRulesCompleteAction(action.leagueId, namespaceData, error);
                break;
            case ActionTypes.LOAD_HELP:
                completeAction = new LoadHelpCompleteAction(action.leagueId, namespaceData, error);
                break;
            case ActionTypes.LOAD_COVERAGE:
                completeAction = new LoadCoverageCompleteAction(action.leagueId, namespaceData, error);
                break;
            case ActionTypes.LOAD_GAME_RULES:
                completeAction = new LoadGameRulesCompleteAction(action.leagueId, namespaceData, error);
                break;
            case ActionTypes.LOAD_SYSTEM_RULES:
                completeAction = new LoadSystemRulesCompleteAction(action.leagueId, namespaceData, error);
                break;
            case ActionTypes.LOAD_GENERAL_RULES:
                completeAction = new LoadGeneralRulesCompleteAction(action.leagueId, namespaceData, error);
                break;
        }
        return completeAction;
    }
}
NamespaceEffects.ɵfac = function NamespaceEffects_Factory(t) { return new (t || NamespaceEffects)(i0.ɵɵinject(i1.LeagueResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
NamespaceEffects.ɵprov = i0.ɵɵdefineInjectable({ token: NamespaceEffects, factory: NamespaceEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], NamespaceEffects.prototype, "loadNamespace$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NamespaceEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.LeagueResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadNamespace$: [] }); })();

const GameAccountActionTypes = {
    LOAD_GAMEACCOUNTS: type("[Gameaccounts] Load Multiple"),
    LOAD_ONCE_GAMEACCOUNTS: loadOnceActionType("[Gameaccounts] Load Multiple Once"),
    LOAD_GAMEACCOUNTS_COMPLETE: type("[Gameaccounts] Load Multiple Complete"),
    ADD_GAMEACCOUNT: type("[Gameaccounts] Add One"),
};
class LoadGameaccountAction {
    constructor(playerId, ignoreCache = false) {
        this.playerId = playerId;
        this.ignoreCache = ignoreCache;
        this.type = GameAccountActionTypes.LOAD_GAMEACCOUNTS;
        this.payload = null;
    }
    get id() {
        return this.playerId;
    }
}
class LoadGameaccountOnceAction extends LoadGameaccountAction {
    constructor() {
        super(...arguments);
        this.type = GameAccountActionTypes.LOAD_ONCE_GAMEACCOUNTS;
    }
}
class LoadGameaccountCompleteAction {
    constructor(playerId, payload, error) {
        this.playerId = playerId;
        this.payload = payload;
        this.error = error;
        this.type = GameAccountActionTypes.LOAD_GAMEACCOUNTS_COMPLETE;
    }
    get id() {
        return this.playerId;
    }
}
class AddGameaccountAction {
    constructor(playerId, payload) {
        this.playerId = playerId;
        this.payload = payload;
        this.type = GameAccountActionTypes.ADD_GAMEACCOUNT;
    }
}

const loadLeaguesReducer = createMultipleLoadReducer(LeagueActionTypes.LOAD_LEAGUE, LeagueActionTypes.LOAD_LEAGUE_COMPLETE);
const reducers$9 = combineReducers({
    leagues: queueReducers([
        loadLeaguesReducer,
        (state, action) => {
            if (action.type === LeagueActionTypes.LOAD_MULTIPLE_LEAGUES) {
                const newAction = action;
                newAction.leagueIds.forEach(leagueId => {
                    state = loadLeaguesReducer(state, new LoadLeagueAction(leagueId));
                });
            }
            if (action.type === LeagueActionTypes.LOAD_MULTIPLE_LEAGUES_COMPLETE) {
                const newAction = action;
                newAction.leagues.forEach(league => {
                    state = loadLeaguesReducer(state, new LoadLeagueCompleteAction(league.id, league, null));
                });
            }
            return state;
        },
    ]),
    participants: createMultipleLoadReducer(LeagueActionTypes.LOAD_PARTICIPANTS, LeagueActionTypes.LOAD_PARTICIPANTS_COMPLETE),
    namespace: leagueNamespaceReducer,
    matches: createMultipleLoadReducer(LeagueActionTypes.LOAD_MATCHES, LeagueActionTypes.LOAD_MATCHES_COMPLETE),
    results: createMultipleLoadReducer(LeagueActionTypes.LOAD_RESULTS, LeagueActionTypes.LOAD_RESULTS_COMPLETE),
    restrictions: queueReducers([
        createNestedMultipleLoadReducer(LeagueActionTypes.LOAD_LEAGUE_RESTRICTIONS, LeagueActionTypes.LOAD_LEAGUE_RESTRICTIONS_COMPLETE),
        (state, action) => {
            if (action.type === GameAccountActionTypes.ADD_GAMEACCOUNT) {
                const laction = action;
                const leagueIds = state.keySeq().toArray();
                leagueIds.forEach(leagueId => {
                    const contestantIds = state
                        .get(leagueId)
                        .keySeq()
                        .toArray();
                    contestantIds.forEach(contestantId => {
                        state = state.updateIn([leagueId, contestantId], contestantState => {
                            const restrictions = contestantState.entry;
                            if (!restrictions) {
                                return contestantState;
                            }
                            return Object.assign(Object.assign({}, contestantState), { entry: restrictions.map(monkeyPatchRestriction.bind(undefined, laction)) });
                        });
                    });
                });
            }
            return state;
        },
    ]),
});
function leagueReducer(state, action) {
    return reducers$9(state, action);
}
function monkeyPatchRestriction(action, restriction) {
    // We only care about the correct restrictions
    if (restriction.type !== "gameaccount" || restriction.params.type !== action.payload.type) {
        return restriction;
    }
    const exists = find(restriction.failedMembers, obj => obj.id === action.playerId);
    if (exists) {
        const failedMembers = filter$1(restriction.failedMembers, obj => obj.id !== action.playerId);
        const passedMembers = [...restriction.passedMembers, action.playerId];
        return Object.assign(Object.assign({}, restriction), { failedMembers: failedMembers, passedMembers: passedMembers, passed: true });
    }
    return restriction;
}

/**
 * @param leagueId
 * @param matches
 */
function normalizeLeagueMatches(leagueId, matches) {
    const actions = [new LoadLeagueMatchesCompleteAction(leagueId, matches.map(match => match.id))];
    forEach(matches, match => {
        actions.push(new LoadMatchCompleteAction(match.id, match));
    });
    return actions;
}
/**
 * @param leagueId
 * @param ranking
 */
function normalizeRanking(leagueId, ranking) {
    const actions = [];
    const teamEntity = new schema.Entity("team");
    const userEntity = new schema.Entity("user");
    const rankingEntity = new schema.Entity("ranking", {
        team: teamEntity,
        user: userEntity,
    });
    const data = normalize(ranking, {
        ranking: [rankingEntity],
    });
    const players = map$1(data.entities.user, user => user);
    if (players && players.length) {
        actions.push(new LoadPlayersCompleteAction(players));
    }
    const teams = map$1(data.entities.team, team => team);
    if (teams && teams.length) {
        actions.push(new LoadTeamsCompleteAction(teams));
    }
    return actions;
}

function getLeagueEntity(state) {
    return state.leagues;
}
function getResults(state) {
    return state.results;
}
function getParticipantEntities(state) {
    return state.participants;
}
function getLeagueRestrictionEntities(state) {
    return state.restrictions;
}
function getLeagueState(state) {
    return state.league;
}
const getLeague = createLoadSelector(compose(getLeagueEntity, getLeagueState));
const getLeagues = createLoadManySelector(compose(getLeagueEntity, getLeagueState));
const getLeagueResults = createLoadSelector(compose(getResults, getLeagueState));
const getLeagueParticipants = createLoadSelector(compose(getParticipantEntities, getLeagueState));
const getLeagueRestrictions = (leagueId, contestantId) => {
    return compose(map => (map ? map.get(contestantId) : null), (map) => map.get(leagueId), getLeagueRestrictionEntities, getLeagueState);
};

class LeagueEffects {
    constructor(leagueResource, participantResource, restrictionResource, actions$, state$) {
        this.leagueResource = leagueResource;
        this.participantResource = participantResource;
        this.restrictionResource = restrictionResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.selectLeague$ = this.actions$.pipe(ofType(LeagueActionTypes.SELECT_LEAGUE), mergeMap((action) => {
            return [
                new LoadLeagueAction(action.leagueId),
                new LoadLeagueMatchesAction(action.leagueId),
                new LoadLeagueResultsAction(action.leagueId),
                new LoadParticipantsAction(action.leagueId),
                new LoadQuickRulesAction(action.leagueId),
            ];
        }));
        this.loadRestrictions$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_LEAGUE_RESTRICTIONS), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_LEAGUE_RESTRICTIONS), filter((action2) => action.leagueId === action2.leagueId && action.contestantId === action2.contestantId), skip(1));
            return this.restrictionResource.getRestrictionsOfLeague(action.leagueId, action.contestantId).pipe(takeUntil(nextSearch$), map(restrictions => new LoadLeagueRestrictionsCompleteAction(action.leagueId, action.contestantId, restrictions)), catchError(error => of(new LoadLeagueRestrictionsCompleteAction(action.leagueId, action.contestantId, null, createErrorObject(error)))));
        }));
        this.loadParticipants$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_PARTICIPANTS), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_PARTICIPANTS), filter((action2) => action.leagueId === action2.leagueId), skip(1));
            return this.participantResource.getParticipantsOfLeague(action.leagueId, "checkedIn").pipe(takeUntil(nextSearch$), map(participants => new LoadParticipantsCompleteAction(action.leagueId, participants)), catchError(error => of(new LoadParticipantsCompleteAction(action.leagueId, null, createErrorObject(error)))));
        }));
        this.loadLeague$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_LEAGUE), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_LEAGUE), filter((action2) => action.leagueId === action2.leagueId), skip(1));
            return this.leagueResource.getLeague(action.leagueId).pipe(takeUntil(nextSearch$), map(leagueR => new LoadLeagueCompleteAction(action.leagueId, leagueR)), catchError(error => of(new LoadLeagueCompleteAction(action.leagueId, null, createErrorObject(error)))));
        }));
        this.loadMatches$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_MATCHES), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_MATCHES), filter((action2) => action.leagueId === action2.leagueId), skip(1));
            return this.leagueResource.getMatchesOfLeague(action.leagueId).pipe(takeUntil(nextSearch$), mergeMap(matches => normalizeLeagueMatches(action.leagueId, matches)), catchError(error => of(new LoadLeagueMatchesCompleteAction(action.leagueId, null, createErrorObject(error)))));
        }));
        this.loadResults$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_RESULTS), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_RESULTS), filter((action2) => action.leagueId === action2.leagueId), skip(1));
            return this.leagueResource.getResultsOfLeague(action.leagueId).pipe(takeUntil(nextSearch$), map(results => new LoadLeagueResultsCompleteAction(action.leagueId, results)), catchError(error => of(new LoadLeagueResultsCompleteAction(action.leagueId, null, createErrorObject(error)))));
        }));
        this.leaveLeague$ = this.actions$.pipe(ofType(LeagueActionTypes.LEAVE_LEAGUE), mergeMap((action) => {
            return this.state$.select(getUser).pipe(filter(entryAvailable), first(), map(data => {
                return new LoadContestantsAction(action.leagueId, +data.entry.id, true);
            }));
        }));
        this.loadParticipantsOnce$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_ONCE_PARTICIPANTS), mergeMap((action) => {
            return this.state$.select(getLeagueParticipants(action.leagueId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadParticipantsAction(action.leagueId)]));
        this.loadResultsOnce$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_ONCE_RESULTS), mergeMap((action) => {
            return this.state$.select(getLeagueResults(action.leagueId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadLeagueResultsAction(action.leagueId)]));
        this.loadLeagueOnce$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_ONCE_LEAGUE), mergeMap((action) => {
            return this.state$.select(getLeague(action.leagueId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadLeagueAction(action.leagueId)]));
        this.loadLeagues$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_MULTIPLE_LEAGUES), mergeMap((action) => {
            if (!action.leagueIds.length) {
                return [];
            }
            return this.leagueResource.queryLeagues({ leagueIds: action.leagueIds }).pipe(first(), map(leagues => new LoadLeaguesCompleteAction(leagues)), catchError(error => {
                return action.leagueIds.map(id => new LoadLeagueCompleteAction(id, null, createErrorObject(error)));
            }));
        }));
        this.loadLeaguesOnce$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_ONCE_MULTIPLE_LEAGUES), mergeMap((action) => {
            if (!action.leagueIds.length) {
                return [];
            }
            return combineLatest(action.leagueIds.map(id => {
                return this.state$.select(getLeague(id)).pipe(first(), map(player => (exists(player) ? undefined : id)));
            })).pipe(map(leagueIds => without(leagueIds, undefined)), first());
        }), filter(leagueIds => Boolean(leagueIds.length)), map(leagueIds => new LoadLeaguesAction(leagueIds)));
        this.loadRestrictionsOnce$ = this.actions$.pipe(ofType(LeagueActionTypes.LOAD_ONCE_LEAGUE_RESTRICTIONS), mergeMap((action) => {
            return this.state$.select(getLeagueRestrictions(action.leagueId, action.contestantId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadLeagueRestrictionsAction(action.leagueId, action.contestantId)]));
    }
}
LeagueEffects.ɵfac = function LeagueEffects_Factory(t) { return new (t || LeagueEffects)(i0.ɵɵinject(i1.LeagueResource), i0.ɵɵinject(i1.ParticipantResource), i0.ɵɵinject(i1.RestrictionResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
LeagueEffects.ɵprov = i0.ɵɵdefineInjectable({ token: LeagueEffects, factory: LeagueEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "selectLeague$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "loadRestrictions$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "loadParticipants$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "loadLeague$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "loadMatches$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "loadResults$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "leaveLeague$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Object)
], LeagueEffects.prototype, "loadParticipantsOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "loadResultsOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "loadLeagueOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "loadLeagues$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "loadLeaguesOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], LeagueEffects.prototype, "loadRestrictionsOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LeagueEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.LeagueResource }, { type: i1.ParticipantResource }, { type: i1.RestrictionResource }, { type: i2.Actions }, { type: i3.Store }]; }, { selectLeague$: [], loadRestrictions$: [], loadParticipants$: [], loadLeague$: [], loadMatches$: [], loadResults$: [], leaveLeague$: [], loadParticipantsOnce$: [], loadResultsOnce$: [], loadLeagueOnce$: [], loadLeagues$: [], loadLeaguesOnce$: [], loadRestrictionsOnce$: [] }); })();

function getGameaccountEntities(state) {
    return state.gameaccounts;
}
function getGameaccountState(state) {
    return state.gameaccount;
}
const getGameaccounts = createLoadSelector(compose(getGameaccountEntities, getGameaccountState));
const getManyGameaccounts = createLoadManySelector(compose(getGameaccountEntities, getGameaccountState));

class GameaccountEffects {
    constructor(playerResource, actions$, state$) {
        this.playerResource = playerResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadGameaccounts$ = this.actions$.pipe(ofType(GameAccountActionTypes.LOAD_GAMEACCOUNTS), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(GameAccountActionTypes.LOAD_GAMEACCOUNTS), filter((action2) => action.playerId === action2.playerId), skip(1));
            return this.playerResource.getGameaccountsOfPlayer(action.playerId).pipe(takeUntil(nextSearch$), map(gameaccounts => {
                return new LoadGameaccountCompleteAction(action.playerId, gameaccounts);
            }), catchError(error => of(new LoadGameaccountCompleteAction(action.playerId, null, createErrorObject(error)))));
        }));
        this.loadGameaccountsOnce$ = this.actions$.pipe(ofType(GameAccountActionTypes.LOAD_ONCE_GAMEACCOUNTS), mergeMap((action) => {
            return this.state$.select(getGameaccounts(action.playerId)).pipe(first(), filter(data => !exists(data)), map(gameaccounts => action));
        }), mergeMap(action => [new LoadGameaccountAction(action.playerId)]));
    }
}
GameaccountEffects.ɵfac = function GameaccountEffects_Factory(t) { return new (t || GameaccountEffects)(i0.ɵɵinject(i1.PlayerResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
GameaccountEffects.ɵprov = i0.ɵɵdefineInjectable({ token: GameaccountEffects, factory: GameaccountEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], GameaccountEffects.prototype, "loadGameaccounts$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], GameaccountEffects.prototype, "loadGameaccountsOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameaccountEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.PlayerResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadGameaccounts$: [], loadGameaccountsOnce$: [] }); })();

class GameAccountModule {
}
GameAccountModule.ɵfac = function GameAccountModule_Factory(t) { return new (t || GameAccountModule)(); };
GameAccountModule.ɵmod = i0.ɵɵdefineNgModule({ type: GameAccountModule });
GameAccountModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([GameaccountEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(GameAccountModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameAccountModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([GameaccountEffects])],
            }]
    }], null, null); })();

class LeagueModule {
}
LeagueModule.ɵfac = function LeagueModule_Factory(t) { return new (t || LeagueModule)(); };
LeagueModule.ɵmod = i0.ɵɵdefineNgModule({ type: LeagueModule });
LeagueModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, GameAccountModule, EffectsModule.forFeature([LeagueEffects, NamespaceEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(LeagueModule, { imports: [CommonModule, GameAccountModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LeagueModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, GameAccountModule, EffectsModule.forFeature([LeagueEffects, NamespaceEffects])],
            }]
    }], null, null); })();

const RosterActionTypes = {
    SELECT_ROSTER: type("[Roster] Select"),
    LOAD_ROSTER: type("[Roster] Load Roster"),
    LOAD_ONCE_ROSTER: loadOnceActionType("[Roster] Load Roster Once"),
    LOAD_ROSTER_COMPLETE: type("[Roster] Load Roster Complete"),
    ADD_MEMBER_TO_ROSTER: type("[Roster] Add Member to Roster"),
    REMOVE_MEMBER_FROM_ROSTER: type("[Roster] Remove Member from Roster"),
    LEAVE_FROM_ROSTER: type("[Roster] Leave Roster"),
    LOAD_ROSTER_HISTORY: type("[Roster] Load History Roster"),
    LOAD_ONCE_ROSTER_HISTORY: loadOnceActionType("[Roster] Load History Roster Once"),
    LOAD_ROSTER_HISTORY_COMPLETE: type("[Roster] Load Roster History Complete"),
};
class SelectRosterAction {
    constructor(leagueId, teamId) {
        this.leagueId = leagueId;
        this.teamId = teamId;
        this.type = RosterActionTypes.SELECT_ROSTER;
        this.payload = null;
    }
}
class LoadRosterAction {
    constructor(leagueId, teamId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.teamId = teamId;
        this.ignoreCache = ignoreCache;
        this.type = RosterActionTypes.LOAD_ROSTER;
        this.payload = null;
    }
    get id() {
        return this.leagueId;
    }
    get id2() {
        return this.teamId;
    }
}
class LoadRosterOnceAction extends LoadRosterAction {
    constructor() {
        super(...arguments);
        this.type = RosterActionTypes.LOAD_ONCE_ROSTER;
    }
}
class LoadRosterCompleteAction {
    constructor(leagueId, teamId, payload, error) {
        this.leagueId = leagueId;
        this.teamId = teamId;
        this.payload = payload;
        this.error = error;
        this.type = RosterActionTypes.LOAD_ROSTER_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
    get id2() {
        return this.teamId;
    }
}
class AddMemberToRosterAction {
    constructor(leagueId, teamId, playerId) {
        this.leagueId = leagueId;
        this.teamId = teamId;
        this.playerId = playerId;
        this.payload = null;
        this.type = RosterActionTypes.ADD_MEMBER_TO_ROSTER;
    }
}
class RemoveMemberFromRosterAction {
    constructor(leagueId, teamId, playerId) {
        this.leagueId = leagueId;
        this.teamId = teamId;
        this.playerId = playerId;
        this.payload = null;
        this.type = RosterActionTypes.REMOVE_MEMBER_FROM_ROSTER;
    }
}
class LeaveRosterAction {
    constructor(leagueId, teamId) {
        this.leagueId = leagueId;
        this.teamId = teamId;
        this.payload = null;
        this.type = RosterActionTypes.LEAVE_FROM_ROSTER;
    }
}
// History
class LoadRosterHistoryAction {
    constructor(leagueId, teamId, ignoreCache = false) {
        this.leagueId = leagueId;
        this.teamId = teamId;
        this.ignoreCache = ignoreCache;
        this.type = RosterActionTypes.LOAD_ROSTER_HISTORY;
        this.payload = null;
    }
    get id() {
        return this.leagueId;
    }
    get id2() {
        return this.teamId;
    }
}
class LoadRosterHistoryOnceAction extends LoadRosterHistoryAction {
    constructor() {
        super(...arguments);
        this.type = RosterActionTypes.LOAD_ONCE_ROSTER_HISTORY;
    }
}
class LoadRosterHistoryCompleteAction {
    constructor(leagueId, teamId, payload, error) {
        this.leagueId = leagueId;
        this.teamId = teamId;
        this.payload = payload;
        this.error = error;
        this.type = RosterActionTypes.LOAD_ROSTER_HISTORY_COMPLETE;
    }
    get id() {
        return this.leagueId;
    }
    get id2() {
        return this.teamId;
    }
}

const reducers$8 = combineReducers({
    rosters: queueReducers([
        createNestedMultipleLoadReducer(RosterActionTypes.LOAD_ROSTER, RosterActionTypes.LOAD_ROSTER_COMPLETE),
        (state, action) => {
            switch (action.type) {
                case RosterActionTypes.ADD_MEMBER_TO_ROSTER:
                    return addMemberToRoster(state, action);
                case RosterActionTypes.REMOVE_MEMBER_FROM_ROSTER:
                    return removeMemberFromRoster(state, action);
                default:
                    return state;
            }
        },
    ]),
    histories: createNestedMultipleLoadReducer(RosterActionTypes.LOAD_ROSTER_HISTORY, RosterActionTypes.LOAD_ROSTER_HISTORY_COMPLETE),
});
function rosterReducer(state, action) {
    return reducers$8(state, action);
}
function addMemberToRoster(state, action) {
    const rosters = state.get(action.leagueId);
    if (!rosters) {
        return state;
    }
    const roster = rosters.get(action.teamId);
    if (!roster) {
        return state;
    }
    const members = roster.entry.members;
    const playerIndex = members.indexOf(action.playerId);
    if (playerIndex !== -1) {
        return state;
    }
    return state.set(action.leagueId, rosters.set(action.teamId, Object.assign(Object.assign({}, roster), { entry: Object.assign(Object.assign({}, roster.entry), { members: [...members, action.playerId] }) })));
}
function removeMemberFromRoster(state, action) {
    const rosters = state.get(action.leagueId);
    if (!rosters) {
        return state;
    }
    const roster = rosters.get(action.teamId);
    if (!roster) {
        return state;
    }
    const members = roster.entry.members;
    const playerIndex = members.indexOf(action.playerId);
    if (playerIndex === -1) {
        return state;
    }
    return state.set(action.leagueId, rosters.set(action.teamId, Object.assign(Object.assign({}, roster), { entry: Object.assign(Object.assign({}, roster.entry), { members: without(members, action.playerId) }) })));
}

function getRosterEntities(state) {
    return state.rosters;
}
function getRosterHistoryEntities(state) {
    return state.histories;
}
function getRosterState(state) {
    return state.roster;
}
function filterByTeamIds(teamIds) {
    return (state) => {
        return state ? state.filter((value, key) => teamIds.indexOf(key) !== -1) : null;
    };
}
function getById(id) {
    return (state) => {
        return state ? state.get(id) : null;
    };
}
const getByTeam = (id) => getById(id);
const getByLeague = (id) => getById(id);
const getRosterHistories = (leagueId) => {
    return compose(getByLeague(leagueId), getRosterHistoryEntities, getRosterState);
};
const getRosterHistory = (leagueId, teamId) => {
    return compose(getByTeam(teamId), getRosterHistories(leagueId));
};
const getRosters = (leagueId) => {
    return compose(getByLeague(leagueId), getRosterEntities, getRosterState);
};
const getRoster = (leagueId, teamId) => {
    return compose(getByTeam(teamId), getRosters(leagueId));
};
const getRostersForTeams = (leagueId, teamIds) => {
    return compose(filterByTeamIds(teamIds), getRosters(leagueId));
};

class RosterEffects {
    constructor(rosterResource, actions$, state$) {
        this.rosterResource = rosterResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.selectRoster$ = this.actions$.pipe(ofType(RosterActionTypes.SELECT_ROSTER), mergeMap((action) => {
            return [
                new LoadRosterAction(action.leagueId, action.teamId),
                new LoadRosterHistoryAction(action.leagueId, action.teamId),
            ];
        }));
        this.loadRoster$ = this.actions$.pipe(ofType(RosterActionTypes.LOAD_ROSTER), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(RosterActionTypes.LOAD_ROSTER), filter((action2) => action.leagueId === action2.leagueId && action.teamId === action2.teamId), skip(1));
            return this.rosterResource.getRoster(action.leagueId, action.teamId).pipe(takeUntil(nextSearch$), map(loadedRoster => new LoadRosterCompleteAction(action.leagueId, action.teamId, loadedRoster)), catchError(error => of(new LoadRosterCompleteAction(action.leagueId, action.teamId, null, createErrorObject(error)))));
        }));
        this.loadRosterOnce$ = this.actions$.pipe(ofType(RosterActionTypes.LOAD_ONCE_ROSTER), mergeMap((action) => {
            return this.state$.select(getRoster(action.leagueId, action.teamId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadRosterAction(action.leagueId, action.teamId)]));
        this.loadRosterHistory$ = this.actions$.pipe(ofType(RosterActionTypes.LOAD_ROSTER_HISTORY), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(RosterActionTypes.LOAD_ROSTER_HISTORY), filter((action2) => action.leagueId === action2.leagueId && action.teamId === action2.teamId), skip(1));
            return this.rosterResource.getRosterHistory(action.leagueId, action.teamId).pipe(takeUntil(nextSearch$), map(rosterHistory => {
                return new LoadRosterHistoryCompleteAction(action.leagueId, action.teamId, rosterHistory);
            }), catchError(error => {
                return of(new LoadRosterHistoryCompleteAction(action.leagueId, action.teamId, null, createErrorObject(error)));
            }));
        }));
        this.loadRosterHistoryOnce$ = this.actions$.pipe(ofType(RosterActionTypes.LOAD_ONCE_ROSTER_HISTORY), mergeMap((action) => {
            return this.state$.select(getRosterHistory(action.leagueId, action.teamId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadRosterHistoryAction(action.leagueId, action.teamId)]));
        this.leaveRoster$ = this.actions$.pipe(ofType(RosterActionTypes.LEAVE_FROM_ROSTER), mergeMap((action) => {
            return this.state$.select(getUser).pipe(filter(entryAvailable), first(), mergeMap(data => {
                return [
                    new RemoveMemberFromRosterAction(action.leagueId, action.teamId, +data.entry.id),
                    new LoadContestantsAction(action.leagueId, +data.entry.id, true),
                    new LoadLeagueRestrictionsAction(action.leagueId, action.teamId, true),
                ];
            }));
        }));
    }
}
RosterEffects.ɵfac = function RosterEffects_Factory(t) { return new (t || RosterEffects)(i0.ɵɵinject(i1.RosterResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
RosterEffects.ɵprov = i0.ɵɵdefineInjectable({ token: RosterEffects, factory: RosterEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], RosterEffects.prototype, "selectRoster$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], RosterEffects.prototype, "loadRoster$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], RosterEffects.prototype, "loadRosterOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], RosterEffects.prototype, "loadRosterHistory$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], RosterEffects.prototype, "loadRosterHistoryOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], RosterEffects.prototype, "leaveRoster$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RosterEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.RosterResource }, { type: i2.Actions }, { type: i3.Store }]; }, { selectRoster$: [], loadRoster$: [], loadRosterOnce$: [], loadRosterHistory$: [], loadRosterHistoryOnce$: [], leaveRoster$: [] }); })();

class RosterModule {
}
RosterModule.ɵfac = function RosterModule_Factory(t) { return new (t || RosterModule)(); };
RosterModule.ɵmod = i0.ɵɵdefineNgModule({ type: RosterModule });
RosterModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([RosterEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(RosterModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RosterModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([RosterEffects])],
            }]
    }], null, null); })();

const MatchSetupActionTypes = {
    LOAD_MATCH_DATE: type("[Match Setup] Date Load"),
    LOAD_ONCE_MATCH_DATE: loadOnceActionType("[Match Setup] Date Load Once"),
    LOAD_MATCH_DATE_COMPLETE: type("[Match Setup] Date Load Complete"),
    LOAD_MATCH_RESULT: type("[Match Setup] Result Load"),
    LOAD_ONCE_MATCH_RESULT: loadOnceActionType("[Match Setup] Result Load Once"),
    LOAD_MATCH_RESULT_COMPLETE: type("[Match Setup] Result Load Complete"),
    LOAD_MATCH_CHALLENGE: type("[Match Setup] Challenge Load"),
    LOAD_ONCE_MATCH_CHALLENGE: loadOnceActionType("[Match Setup] Challenge Load Once"),
    LOAD_MATCH_CHALLENGE_COMPLETE: type("[Match Setup] Challenge Load Complete"),
};
class LoadMatchDateReportAction {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = MatchSetupActionTypes.LOAD_MATCH_DATE;
        this.payload = null;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchDateReportOnceAction extends LoadMatchDateReportAction {
    constructor() {
        super(...arguments);
        this.type = MatchSetupActionTypes.LOAD_ONCE_MATCH_DATE;
    }
}
class LoadMatchDateReportCompleteAction {
    constructor(matchId, payload, error) {
        this.matchId = matchId;
        this.payload = payload;
        this.error = error;
        this.type = MatchSetupActionTypes.LOAD_MATCH_DATE_COMPLETE;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchResultReportAction {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = MatchSetupActionTypes.LOAD_MATCH_RESULT;
        this.payload = null;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchResultReportOnceAction extends LoadMatchResultReportAction {
    constructor() {
        super(...arguments);
        this.type = MatchSetupActionTypes.LOAD_ONCE_MATCH_RESULT;
    }
}
class LoadMatchResultReportCompleteAction {
    constructor(matchId, payload, error) {
        this.matchId = matchId;
        this.payload = payload;
        this.error = error;
        this.type = MatchSetupActionTypes.LOAD_MATCH_RESULT_COMPLETE;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchChallengeReportAction {
    constructor(matchId, ignoreCache = false) {
        this.matchId = matchId;
        this.ignoreCache = ignoreCache;
        this.type = MatchSetupActionTypes.LOAD_MATCH_CHALLENGE;
        this.payload = null;
    }
    get id() {
        return this.matchId;
    }
}
class LoadMatchChallengeReportOnceAction extends LoadMatchChallengeReportAction {
    constructor() {
        super(...arguments);
        this.type = MatchSetupActionTypes.LOAD_ONCE_MATCH_CHALLENGE;
    }
}
class LoadMatchChallengeReportCompleteAction {
    constructor(matchId, payload, error) {
        this.matchId = matchId;
        this.payload = payload;
        this.error = error;
        this.type = MatchSetupActionTypes.LOAD_MATCH_CHALLENGE_COMPLETE;
    }
    get id() {
        return this.matchId;
    }
}

const reducers$7 = combineReducers({
    date: createMultipleLoadReducer(MatchSetupActionTypes.LOAD_MATCH_DATE, MatchSetupActionTypes.LOAD_MATCH_DATE_COMPLETE),
    result: createMultipleLoadReducer(MatchSetupActionTypes.LOAD_MATCH_RESULT, MatchSetupActionTypes.LOAD_MATCH_RESULT_COMPLETE),
    challenge: createMultipleLoadReducer(MatchSetupActionTypes.LOAD_MATCH_CHALLENGE, MatchSetupActionTypes.LOAD_MATCH_CHALLENGE_COMPLETE),
});
function matchSetupReducer(state, action) {
    return reducers$7(state, action);
}

function getMatchDateReportEntity(state) {
    return state.date;
}
function getMatchResultReportEntity(state) {
    return state.result;
}
function getMatchChallengeReportEntity(state) {
    return state.challenge;
}
function getMatchSetupState(state) {
    return state.setup;
}
function getMatchState$1(state) {
    return state.match;
}
const getMatchDateReport = createLoadSelector(compose(getMatchDateReportEntity, getMatchSetupState, getMatchState$1));
const getMatchResultReport = createLoadSelector(compose(getMatchResultReportEntity, getMatchSetupState, getMatchState$1));
const getMatchChallengeReport = createLoadSelector(compose(getMatchChallengeReportEntity, getMatchSetupState, getMatchState$1));

class MatchSetupEffects {
    constructor(matches, actions$, state$) {
        this.matches = matches;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadDateReport$ = this.actions$.pipe(ofType(MatchSetupActionTypes.LOAD_MATCH_DATE), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(MatchSetupActionTypes.LOAD_MATCH_DATE), filter((action2) => action.matchId === action2.matchId), skip(1));
            return this.matches.getSetupReportOfMatch(action.matchId).pipe(takeUntil(nextSearch$), map(report => new LoadMatchDateReportCompleteAction(action.matchId, report)), catchError(error => of(new LoadMatchDateReportCompleteAction(action.matchId, null, createErrorObject(error)))));
        }));
        this.loadDateReportOnce$ = this.actions$.pipe(ofType(MatchSetupActionTypes.LOAD_ONCE_MATCH_DATE), mergeMap((action) => {
            return this.state$.select(getMatchDateReport(action.matchId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadMatchDateReportAction(action.matchId)]));
        this.loadResultReport$ = this.actions$.pipe(ofType(MatchSetupActionTypes.LOAD_MATCH_RESULT), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(MatchSetupActionTypes.LOAD_MATCH_RESULT), filter((action2) => action.matchId === action2.matchId), skip(1));
            return this.matches.getResultReportOfMatch(action.matchId).pipe(takeUntil(nextSearch$), map(report => new LoadMatchResultReportCompleteAction(action.matchId, report)), catchError(error => of(new LoadMatchResultReportCompleteAction(action.matchId, null, createErrorObject(error)))));
        }));
        this.loadResultReportOnce$ = this.actions$.pipe(ofType(MatchSetupActionTypes.LOAD_ONCE_MATCH_RESULT), mergeMap((action) => {
            return this.state$.select(getMatchResultReport(action.matchId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadMatchResultReportAction(action.matchId)]));
        this.loadChallengeReport$ = this.actions$.pipe(ofType(MatchSetupActionTypes.LOAD_MATCH_CHALLENGE), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(MatchSetupActionTypes.LOAD_MATCH_CHALLENGE), filter((action2) => action.matchId === action2.matchId), skip(1));
            return this.matches.getChallengeReportOfMatch(action.matchId).pipe(takeUntil(nextSearch$), map(report => new LoadMatchChallengeReportCompleteAction(action.matchId, report)), catchError(error => of(new LoadMatchChallengeReportCompleteAction(action.matchId, null, createErrorObject(error)))));
        }));
        this.loadChallengeReportOnce$ = this.actions$.pipe(ofType(MatchSetupActionTypes.LOAD_ONCE_MATCH_CHALLENGE), mergeMap((action) => {
            return this.state$.select(getMatchChallengeReport(action.matchId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadMatchChallengeReportAction(action.matchId)]));
    }
}
MatchSetupEffects.ɵfac = function MatchSetupEffects_Factory(t) { return new (t || MatchSetupEffects)(i0.ɵɵinject(i1.MatchResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
MatchSetupEffects.ɵprov = i0.ɵɵdefineInjectable({ token: MatchSetupEffects, factory: MatchSetupEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchSetupEffects.prototype, "loadDateReport$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchSetupEffects.prototype, "loadDateReportOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchSetupEffects.prototype, "loadResultReport$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchSetupEffects.prototype, "loadResultReportOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchSetupEffects.prototype, "loadChallengeReport$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchSetupEffects.prototype, "loadChallengeReportOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchSetupEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.MatchResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadDateReport$: [], loadDateReportOnce$: [], loadResultReport$: [], loadResultReportOnce$: [], loadChallengeReport$: [], loadChallengeReportOnce$: [] }); })();

const reducers$6 = combineReducers({
    matches: createMultipleLoadReducer(MatchActionTypes.LOAD_MATCH, MatchActionTypes.LOAD_MATCH_COMPLETE),
    media: createMultipleLoadReducer(MatchActionTypes.LOAD_MEDIA, MatchActionTypes.LOAD_MEDIA_COMPLETE),
    setup: matchSetupReducer,
});
function matchReducer(state, action) {
    return reducers$6(state, action);
}

/**
 * @param matchId
 * @param match
 */
function normalizeMatch(matchId, match) {
    const actions = [];
    const teamEntity = new schema.Entity("team");
    const userEntity = new schema.Entity("user");
    const contestantsEntity = new schema.Entity("contestants", {
        team: teamEntity,
        user: userEntity,
    });
    const data = normalize(match, {
        contestants: [contestantsEntity],
    });
    const players = map$1(data.entities.user, user => user);
    if (players && players.length) {
        actions.push(new LoadPlayersCompleteAction(players));
    }
    const teams = map$1(data.entities.team, team => team);
    if (teams && teams.length) {
        actions.push(new LoadTeamsCompleteAction(teams));
    }
    return actions;
}

function getMatchEntity(state) {
    return state.matches;
}
function getMediaEntity(state) {
    return state.media;
}
function getMatchState(state) {
    return state.match;
}
const getMatch = createLoadSelector(compose(getMatchEntity, getMatchState));
const getMatchMedia = createLoadSelector(compose(getMediaEntity, getMatchState));

class MatchEffects {
    constructor(matches, actions$, state$) {
        this.matches = matches;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadMatch$ = this.actions$.pipe(ofType(MatchActionTypes.LOAD_MATCH), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(MatchActionTypes.LOAD_MATCH), filter((action2) => action.matchId === action2.matchId), skip(1));
            return this.matches.getMatch(action.matchId).pipe(takeUntil(nextSearch$), map(match => new LoadMatchCompleteAction(action.matchId, match)), catchError(error => of(new LoadMatchCompleteAction(action.matchId, null, createErrorObject(error)))));
        }));
        this.loadMatchComplete$ = this.actions$.pipe(ofType(MatchActionTypes.LOAD_MATCH_COMPLETE), filter((action) => Boolean(action.payload)), mergeMap((action) => normalizeMatch(action.matchId, action.payload)));
        this.loadMatchOnce$ = this.actions$.pipe(ofType(MatchActionTypes.LOAD_ONCE_MATCH), mergeMap((action) => {
            return this.state$.select(getMatch(action.matchId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadMatchAction(action.matchId)]));
        this.loadMedia$ = this.actions$.pipe(ofType(MatchActionTypes.LOAD_MEDIA), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(MatchActionTypes.LOAD_MEDIA), filter((action2) => action.matchId === action2.matchId), skip(1));
            return this.matches.getMatchmediaOfMatch(action.matchId).pipe(takeUntil(nextSearch$), map(media => new LoadMediaCompleteAction(action.matchId, media)), catchError(error => of(new LoadMediaCompleteAction(action.matchId, null, createErrorObject(error)))));
        }));
        this.loadMediaOnce$ = this.actions$.pipe(ofType(MatchActionTypes.LOAD_ONCE_MEDIA), mergeMap((action) => {
            return this.state$.select(getMatchMedia(action.matchId)).pipe(first(), filter(data => !exists(data)), map(data => action));
        }), mergeMap(action => [new LoadMediaAction(action.matchId)]));
    }
}
MatchEffects.ɵfac = function MatchEffects_Factory(t) { return new (t || MatchEffects)(i0.ɵɵinject(i1.MatchResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
MatchEffects.ɵprov = i0.ɵɵdefineInjectable({ token: MatchEffects, factory: MatchEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchEffects.prototype, "loadMatch$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchEffects.prototype, "loadMatchComplete$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchEffects.prototype, "loadMatchOnce$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchEffects.prototype, "loadMedia$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MatchEffects.prototype, "loadMediaOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.MatchResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadMatch$: [], loadMatchComplete$: [], loadMatchOnce$: [], loadMedia$: [], loadMediaOnce$: [] }); })();

class MatchModule {
}
MatchModule.ɵfac = function MatchModule_Factory(t) { return new (t || MatchModule)(); };
MatchModule.ɵmod = i0.ɵɵdefineNgModule({ type: MatchModule });
MatchModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([MatchEffects, MatchSetupEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(MatchModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([MatchEffects, MatchSetupEffects])],
            }]
    }], null, null); })();

const UserActionTypes = {
    SELECT_USER: type("[User] Select"),
    LOAD_USER: type("[User] Private Profile Load"),
    LOAD_USER_COMPLETE: type("[User] Private Profile Load Complete"),
    LOAD_USER_PREMIUM: type("[User] Premium Load"),
    LOAD_USER_PREMIUM_COMPLETE: type("[User] Premium Load Complete"),
    LOAD_USER_TEAMS: type("[User] Teams Load"),
    LOAD_USER_TEAMS_COMPLETE: type("[User] Teams Load Complete"),
    LOAD_USER_SHOP_DISCOUNT_CODES: type("[User] Shop Discount Codes Load"),
    LOAD_USER_SHOP_DISCOUNT_CODES_COMPLETE: type("[User] Shop Discount Codes Load Complete"),
};
class SelectUserAction {
    constructor(userId) {
        this.userId = userId;
        this.type = UserActionTypes.SELECT_USER;
        this.payload = null;
    }
}
class LoadUserAction {
    constructor(userId, ignoreCache = false) {
        this.userId = userId;
        this.ignoreCache = ignoreCache;
        this.type = UserActionTypes.LOAD_USER;
        this.payload = null;
    }
    get id() {
        return this.userId;
    }
}
class LoadUserCompleteAction {
    constructor(userId, payload, error) {
        this.userId = userId;
        this.payload = payload;
        this.error = error;
        this.type = UserActionTypes.LOAD_USER_COMPLETE;
    }
    get id() {
        return this.userId;
    }
}
// Premium
class LoadUserPremiumAction {
    constructor(userId, ignoreCache = false) {
        this.userId = userId;
        this.ignoreCache = ignoreCache;
        this.type = UserActionTypes.LOAD_USER_PREMIUM;
        this.payload = null;
    }
    get id() {
        return this.userId;
    }
}
class LoadUserPremiumCompleteAction {
    constructor(userId, payload, error) {
        this.userId = userId;
        this.payload = payload;
        this.error = error;
        this.type = UserActionTypes.LOAD_USER_PREMIUM_COMPLETE;
    }
    get id() {
        return this.userId;
    }
}
class LoadUserShopDiscountCodesAction {
    constructor(userId, ignoreCache = false) {
        this.userId = userId;
        this.ignoreCache = ignoreCache;
        this.type = UserActionTypes.LOAD_USER_SHOP_DISCOUNT_CODES;
    }
    get id() {
        return this.userId;
    }
}
class LoadUserShopDiscountCodesCompleteAction {
    constructor(userId, payload, error) {
        this.userId = userId;
        this.payload = payload;
        this.error = error;
        this.type = UserActionTypes.LOAD_USER_SHOP_DISCOUNT_CODES_COMPLETE;
    }
    get id() {
        return this.userId;
    }
}
// Premium
class LoadUserTeamsAction {
    constructor(userId, ignoreCache = false) {
        this.userId = userId;
        this.ignoreCache = ignoreCache;
        this.type = UserActionTypes.LOAD_USER_TEAMS;
        this.payload = null;
    }
    get id() {
        return this.userId;
    }
}
class LoadUserTeamsCompleteAction {
    constructor(userId, payload, error) {
        this.userId = userId;
        this.payload = payload;
        this.error = error;
        this.type = UserActionTypes.LOAD_USER_TEAMS_COMPLETE;
    }
    get id() {
        return this.userId;
    }
}

const reducers$5 = combineReducers({
    user: createLoadReducer(UserActionTypes.LOAD_USER, UserActionTypes.LOAD_USER_COMPLETE),
    premium: createLoadReducer(UserActionTypes.LOAD_USER_PREMIUM, UserActionTypes.LOAD_USER_PREMIUM_COMPLETE),
    teams: createLoadReducer(UserActionTypes.LOAD_USER_TEAMS, UserActionTypes.LOAD_USER_TEAMS_COMPLETE),
    shopifyCodes: createLoadReducer(UserActionTypes.LOAD_USER_SHOP_DISCOUNT_CODES, UserActionTypes.LOAD_USER_SHOP_DISCOUNT_CODES_COMPLETE),
});
function userReducer(state, action) {
    return reducers$5(state, action);
}

function normalizeUserTeams(userId, memberships) {
    const actions = [];
    const teams = [];
    forEach(memberships, membership => {
        teams.push(membership.team);
    });
    if (teams.length) {
        actions.push(new LoadTeamsCompleteAction(teams));
    }
    return actions;
}

class UserEffects {
    constructor(userResource, actions$) {
        this.userResource = userResource;
        this.actions$ = actions$;
        this.loadUser$ = this.actions$.pipe(ofType(UserActionTypes.LOAD_USER), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(UserActionTypes.LOAD_USER), filter((action2) => action.userId === action2.userId), skip(1));
            return this.userResource.getUser().pipe(takeUntil(nextSearch$), map(userR => new LoadUserCompleteAction(action.userId, userR)), catchError(error => of(new LoadUserCompleteAction(action.userId, null, createErrorObject(error)))));
        }));
        this.loadPremium$ = this.actions$.pipe(ofType(UserActionTypes.LOAD_USER_PREMIUM), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(UserActionTypes.LOAD_USER_PREMIUM), filter((action2) => action.userId === action2.userId), skip(1));
            return this.userResource.getPremiumDaysOfUser().pipe(takeUntil(nextSearch$), map(response => new Date(response.until)), map(until => new LoadUserPremiumCompleteAction(action.userId, { until: until })), catchError(error => of(new LoadUserPremiumCompleteAction(action.userId, null, createErrorObject(error)))));
        }));
        this.loadUserShopDiscountCodes$ = this.actions$.pipe(ofType(UserActionTypes.LOAD_USER_SHOP_DISCOUNT_CODES), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(UserActionTypes.LOAD_USER_SHOP_DISCOUNT_CODES), filter((action2) => action.userId === action2.userId), skip(1));
            return this.userResource.createShopDiscountCodesForUser().pipe(takeUntil(nextSearch$), map(codes => new LoadUserShopDiscountCodesCompleteAction(action.userId, codes)), catchError(error => of(new LoadUserShopDiscountCodesCompleteAction(action.userId, null, createErrorObject(error)))));
        }));
        this.loadUserTeams$ = this.actions$.pipe(ofType(UserActionTypes.LOAD_USER_TEAMS), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(UserActionTypes.LOAD_USER_TEAMS), filter((action2) => action.userId === action2.userId), skip(1));
            return this.userResource.getTeamsOfUser().pipe(takeUntil(nextSearch$), map(userTeams => new LoadUserTeamsCompleteAction(action.userId, userTeams)), catchError(error => of(new LoadUserTeamsCompleteAction(action.userId, null, createErrorObject(error)))));
        }));
        this.selectUser$ = this.actions$.pipe(ofType(UserActionTypes.SELECT_USER), mergeMap((action) => {
            return [
                new LoadUserAction(action.userId),
                new LoadUserPremiumAction(action.userId),
                new LoadUserTeamsAction(action.userId),
                new SelectPlayerAction(action.userId),
            ];
        }));
        this.loadUserTeamsComplete$ = this.actions$.pipe(ofType(UserActionTypes.LOAD_USER_TEAMS_COMPLETE), filter((action) => !Boolean(action.error)), mergeMap((action) => normalizeUserTeams(action.userId, action.payload)));
    }
}
UserEffects.ɵfac = function UserEffects_Factory(t) { return new (t || UserEffects)(i0.ɵɵinject(i1.UserResource), i0.ɵɵinject(i2.Actions)); };
UserEffects.ɵprov = i0.ɵɵdefineInjectable({ token: UserEffects, factory: UserEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], UserEffects.prototype, "loadUser$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], UserEffects.prototype, "loadPremium$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], UserEffects.prototype, "loadUserShopDiscountCodes$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], UserEffects.prototype, "loadUserTeams$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], UserEffects.prototype, "selectUser$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], UserEffects.prototype, "loadUserTeamsComplete$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.UserResource }, { type: i2.Actions }]; }, { loadUser$: [], loadPremium$: [], loadUserShopDiscountCodes$: [], loadUserTeams$: [], selectUser$: [], loadUserTeamsComplete$: [] }); })();

class UserModule {
}
UserModule.ɵfac = function UserModule_Factory(t) { return new (t || UserModule)(); };
UserModule.ɵmod = i0.ɵɵdefineNgModule({ type: UserModule });
UserModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, GameAccountModule, EffectsModule.forFeature([UserEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(UserModule, { imports: [CommonModule, GameAccountModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, GameAccountModule, EffectsModule.forFeature([UserEffects])],
            }]
    }], null, null); })();

const GameAccountTypeActionTypes = {
    LOAD_GAMEACCOUNT_DESCRIPTIONS: type("[Gameaccount Type] Descriptions Load"),
    LOAD_GAMEACCOUNT_DESCRIPTIONS_COMPLETE: type("[Gameaccount Type] Descriptions Load Complete"),
    LOAD_GAMEACCOUNT_PROVIDERS: type("[Gameaccount Type] Provider Load"),
    LOAD_GAMEACCOUNT_PROVIDERS_COMPLETE: type("[Gameaccount Type] Provider Load Complete"),
};
class LoadGameaccountDescriptionsAction {
    constructor(ignoreCache = false) {
        this.ignoreCache = ignoreCache;
        this.payload = null;
        this.type = GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_DESCRIPTIONS;
    }
}
class LoadGameaccountDescriptionsCompleteAction {
    constructor(payload) {
        this.payload = payload;
        this.type = GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_DESCRIPTIONS_COMPLETE;
    }
}
class LoadGameaccountProvidersAction {
    constructor(gameAccountTypeNames, ignoreCache = false) {
        this.gameAccountTypeNames = gameAccountTypeNames;
        this.ignoreCache = ignoreCache;
        this.payload = null;
        this.type = GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_PROVIDERS;
    }
}
class LoadGameaccountProvidersCompleteAction {
    constructor(payload) {
        this.payload = payload;
        this.type = GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_PROVIDERS_COMPLETE;
    }
}

const reducers$4 = combineReducers({
    descriptions: createLoadReducer(GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_DESCRIPTIONS, GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_DESCRIPTIONS_COMPLETE),
    providers: createLoadReducer(GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_PROVIDERS, GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_PROVIDERS_COMPLETE),
});
function gameAccountTypeReducer(state, action) {
    return reducers$4(state, action);
}

class GameAccountTypeEffects {
    constructor(gameaccountTypeResource, actions$) {
        this.gameaccountTypeResource = gameaccountTypeResource;
        this.actions$ = actions$;
        this.loadProviders$ = this.actions$.pipe(ofType(GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_PROVIDERS), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_PROVIDERS), skip(1));
            if (action.gameAccountTypeNames.length === 0) {
                return of(new LoadGameaccountProvidersCompleteAction([]));
            }
            return combineLatest(action.gameAccountTypeNames.map(gameAccountTypeName => {
                return this.gameaccountTypeResource.getProviderOfType(gameAccountTypeName).pipe(catchError(error => {
                    // We expect them to 404
                    if (error.errorCode && error.errorCode === 404) {
                        return of(null);
                    }
                    return throwError(error);
                }));
            })).pipe(takeUntil(nextSearch$), map((providers) => {
                return new LoadGameaccountProvidersCompleteAction(providers.filter(Boolean));
            }));
        }));
        this.loadDescriptions$ = this.actions$.pipe(ofType(GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_DESCRIPTIONS), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(GameAccountTypeActionTypes.LOAD_GAMEACCOUNT_DESCRIPTIONS), skip(1));
            return this.gameaccountTypeResource.getDescriptions().pipe(takeUntil(nextSearch$), map((descriptions) => {
                return new LoadGameaccountDescriptionsCompleteAction(descriptions);
            }));
        }));
    }
}
GameAccountTypeEffects.ɵfac = function GameAccountTypeEffects_Factory(t) { return new (t || GameAccountTypeEffects)(i0.ɵɵinject(i1.GameaccountTypeResource), i0.ɵɵinject(i2.Actions)); };
GameAccountTypeEffects.ɵprov = i0.ɵɵdefineInjectable({ token: GameAccountTypeEffects, factory: GameAccountTypeEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], GameAccountTypeEffects.prototype, "loadProviders$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], GameAccountTypeEffects.prototype, "loadDescriptions$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameAccountTypeEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.GameaccountTypeResource }, { type: i2.Actions }]; }, { loadProviders$: [], loadDescriptions$: [] }); })();

function getDescriptions(state) {
    return state.descriptions;
}
function getProviders(state) {
    return state.providers;
}
function getGameAccountState(state) {
    return state.gameaccountType;
}
const getGameAccountDescriptions = compose(getDescriptions, getGameAccountState);
const getGameAccountProviders = compose(getProviders, getGameAccountState);

class GameAccountTypeModule {
}
GameAccountTypeModule.ɵfac = function GameAccountTypeModule_Factory(t) { return new (t || GameAccountTypeModule)(); };
GameAccountTypeModule.ɵmod = i0.ɵɵdefineNgModule({ type: GameAccountTypeModule });
GameAccountTypeModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([GameAccountTypeEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(GameAccountTypeModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameAccountTypeModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([GameAccountTypeEffects])],
            }]
    }], null, null); })();

const reducers$3 = combineReducers({
    gameaccounts: queueReducers([
        createMultipleLoadReducer(GameAccountActionTypes.LOAD_GAMEACCOUNTS, GameAccountActionTypes.LOAD_GAMEACCOUNTS_COMPLETE),
        (state, action) => {
            if (action.type === GameAccountActionTypes.ADD_GAMEACCOUNT) {
                const laction = action;
                const entryState = state.get(laction.playerId);
                const gameaccounts = entryState.entry;
                if (!gameaccounts) {
                    return state;
                }
                const newGameaccounts = map$1(gameaccounts, gameaccount => {
                    if (gameaccount.type === laction.payload.type) {
                        return Object.assign(Object.assign({}, gameaccount), { value: laction.payload.value });
                    }
                    return gameaccount;
                });
                if (!find(gameaccounts, gameaccount => gameaccount.type === laction.payload.type)) {
                    newGameaccounts.push(laction.payload);
                }
                return state.set(laction.playerId, Object.assign(Object.assign({}, entryState), { entry: newGameaccounts }));
            }
            return state;
        },
    ]),
});
function gameaccountReducer(state, action) {
    return reducers$3(state, action);
}

const GameActionTypes = {
    LOAD_GAME: type("[Game] Load"),
    LOAD_ONCE_GAME: type("[Game] Load Once"),
    LOAD_GAME_COMPLETE: type("[Game] Load Complete"),
};
class LoadGameAction {
    constructor(gameId, ignoreCache = false) {
        this.gameId = gameId;
        this.ignoreCache = ignoreCache;
        this.type = GameActionTypes.LOAD_GAME;
        this.payload = null;
    }
    get id() {
        return this.gameId;
    }
}
class LoadGameOnceAction extends LoadGameAction {
    constructor() {
        super(...arguments);
        this.type = GameActionTypes.LOAD_ONCE_GAME;
    }
}
class LoadGameCompleteAction {
    constructor(gameId, payload, error) {
        this.gameId = gameId;
        this.payload = payload;
        this.error = error;
        this.type = GameActionTypes.LOAD_GAME_COMPLETE;
    }
    get id() {
        return this.gameId;
    }
}

const reducers$2 = combineReducers({
    games: createMultipleLoadReducer(GameActionTypes.LOAD_GAME, GameActionTypes.LOAD_GAME_COMPLETE),
});
function gameReducer(state, action) {
    return reducers$2(state, action);
}

function getGameEntity(state) {
    return state.games;
}
function getGameState(state) {
    return state.game;
}
const getGame = createLoadSelector(compose(getGameEntity, getGameState));

class GameEffects {
    constructor(gameResource, actions$, state$) {
        this.gameResource = gameResource;
        this.actions$ = actions$;
        this.state$ = state$;
        this.loadGame$ = this.actions$.pipe(ofType(GameActionTypes.LOAD_GAME), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(GameActionTypes.LOAD_GAME), filter((action2) => action.gameId === action2.gameId), skip(1));
            return this.gameResource.getGame(action.gameId).pipe(takeUntil(nextSearch$), map(game => {
                return new LoadGameCompleteAction(action.gameId, game);
            }), catchError(error => of(new LoadGameCompleteAction(action.gameId, null, createErrorObject(error)))));
        }));
        this.loadGameOnce$ = this.actions$.pipe(ofType(GameActionTypes.LOAD_ONCE_GAME), mergeMap((action) => {
            return this.state$.select(getGame(action.gameId)).pipe(first(), filter(data => !exists(data)), map(game => action));
        }), mergeMap(action => [new LoadGameAction(action.gameId)]));
    }
}
GameEffects.ɵfac = function GameEffects_Factory(t) { return new (t || GameEffects)(i0.ɵɵinject(i1.GameResource), i0.ɵɵinject(i2.Actions), i0.ɵɵinject(i3.Store)); };
GameEffects.ɵprov = i0.ɵɵdefineInjectable({ token: GameEffects, factory: GameEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], GameEffects.prototype, "loadGame$", void 0);
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], GameEffects.prototype, "loadGameOnce$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.GameResource }, { type: i2.Actions }, { type: i3.Store }]; }, { loadGame$: [], loadGameOnce$: [] }); })();

class GameModule {
}
GameModule.ɵfac = function GameModule_Factory(t) { return new (t || GameModule)(); };
GameModule.ɵmod = i0.ɵɵdefineNgModule({ type: GameModule });
GameModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[GameApiModule, CommonModule, EffectsModule.forFeature([GameEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(GameModule, { imports: [GameApiModule, CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GameModule, [{
        type: NgModule,
        args: [{
                imports: [GameApiModule, CommonModule, EffectsModule.forFeature([GameEffects])],
            }]
    }], null, null); })();

const MetaRankActionTypes = {
    LOAD_RANKING: type("[MetaRank] Load MetaRank Ranking"),
    LOAD_RANKING_COMPLETE: type("[MetaRank] Load MetaRank Ranking Complete"),
};
// Ranking
class LoadMetaRankRankingAction {
    constructor(series, season, limit, offset, ignoreCache = false) {
        this.series = series;
        this.season = season;
        this.limit = limit;
        this.offset = offset;
        this.ignoreCache = ignoreCache;
        this.type = MetaRankActionTypes.LOAD_RANKING;
        this.payload = null;
    }
    get id() {
        return `${this.series}-${this.season}`;
    }
}
class LoadMetaRankRankingCompleteAction {
    constructor(series, season, payload, error) {
        this.series = series;
        this.season = season;
        this.payload = payload;
        this.error = error;
        this.type = MetaRankActionTypes.LOAD_RANKING_COMPLETE;
    }
    get id() {
        return `${this.series}-${this.season}`;
    }
}

const reducers$1 = combineReducers({
    ranking: createMultipleLoadReducer(MetaRankActionTypes.LOAD_RANKING, MetaRankActionTypes.LOAD_RANKING_COMPLETE),
});
function metarankReducer(state, action) {
    return reducers$1(state, action);
}

class MetaRankEffects {
    constructor(metarankResource, actions$) {
        this.metarankResource = metarankResource;
        this.actions$ = actions$;
        this.loadMetaRankRanking$ = this.actions$.pipe(ofType(MetaRankActionTypes.LOAD_RANKING), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(MetaRankActionTypes.LOAD_RANKING), filter((action2) => action.id === action2.id), skip(1));
            return this.metarankResource.getRankingForSeries(action.series, action.season, action.limit, action.offset).pipe(takeUntil(nextSearch$), map((rank) => new LoadMetaRankRankingCompleteAction(action.series, action.season, rank)), catchError(error => of(new LoadMetaRankRankingCompleteAction(action.series, action.season, null, createErrorObject(error)))));
        }));
    }
}
MetaRankEffects.ɵfac = function MetaRankEffects_Factory(t) { return new (t || MetaRankEffects)(i0.ɵɵinject(i1.MetaRankResource), i0.ɵɵinject(i2.Actions)); };
MetaRankEffects.ɵprov = i0.ɵɵdefineInjectable({ token: MetaRankEffects, factory: MetaRankEffects.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Observable)
], MetaRankEffects.prototype, "loadMetaRankRanking$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MetaRankEffects, [{
        type: Injectable
    }], function () { return [{ type: i1.MetaRankResource }, { type: i2.Actions }]; }, { loadMetaRankRanking$: [] }); })();

function getRanking(state) {
    return state.ranking;
}
function getMetaRankState(state) {
    return state.metarank;
}
const getMetaRankRanking = createLoadSelector(compose(getRanking, getMetaRankState));

class MetaRankModule {
}
MetaRankModule.ɵfac = function MetaRankModule_Factory(t) { return new (t || MetaRankModule)(); };
MetaRankModule.ɵmod = i0.ɵɵdefineNgModule({ type: MetaRankModule });
MetaRankModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[CommonModule, EffectsModule.forFeature([MetaRankEffects])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(MetaRankModule, { imports: [CommonModule, i2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MetaRankModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([MetaRankEffects])],
            }]
    }], null, null); })();

class StateModule {
}
StateModule.ɵfac = function StateModule_Factory(t) { return new (t || StateModule)(); };
StateModule.ɵmod = i0.ɵɵdefineNgModule({ type: StateModule });
StateModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[
            CommonModule,
            ContestantModule,
            GameAccountModule,
            GameAccountTypeModule,
            GameintegrationModule,
            LeagueModule,
            MatchModule,
            PlayerModule,
            RosterModule,
            TeamModule,
            UserModule,
            GameModule,
            MetaRankModule,
        ]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(StateModule, { imports: [CommonModule,
        ContestantModule,
        GameAccountModule,
        GameAccountTypeModule,
        GameintegrationModule,
        LeagueModule,
        MatchModule,
        PlayerModule,
        RosterModule,
        TeamModule,
        UserModule,
        GameModule,
        MetaRankModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StateModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    ContestantModule,
                    GameAccountModule,
                    GameAccountTypeModule,
                    GameintegrationModule,
                    LeagueModule,
                    MatchModule,
                    PlayerModule,
                    RosterModule,
                    TeamModule,
                    UserModule,
                    GameModule,
                    MetaRankModule,
                ],
            }]
    }], null, null); })();

const reducers = {
    league: leagueReducer,
    match: matchReducer,
    contestant: contestantReducer,
    roster: rosterReducer,
    gameaccount: gameaccountReducer,
    gameaccountType: gameAccountTypeReducer,
    team: teamReducer,
    player: playerReducer,
    user: userReducer,
    gameintegration: gameintegrationReducer,
    game: gameReducer,
    metarank: metarankReducer,
};

/*
 * Public API Surface of state
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ActionTypes, AddGameaccountAction, AddMemberToRosterAction, AutochessModule, CocModule, ContestantActionTypes, ContestantEffects, ContestantModule, FortniteModule, GameAccountActionTypes, GameAccountModule, GameAccountTypeActionTypes, GameAccountTypeEffects, GameAccountTypeModule, GameActionTypes, GameEffects, GameModule, GameaccountEffects, GameintegrationModule, LOAD_ONCE_ACTION_TYPES, LeagueActionTypes, LeagueEffects, LeagueModule, LeaveLeagueAction, LeaveRosterAction, LoadAutochessMatchAction, LoadChatInfoAction, LoadChatInfoCompleteAction, LoadContestantClanAction as LoadCocContestantClanAction, LoadContestantClanCompleteAction as LoadCocContestantClanCompleteAction, LoadContestantClanOnceAction as LoadCocContestantClanOnceAction, LoadContestantsAction, LoadContestantsCompleteAction, LoadContestantsOnceAction, LoadCoverageAction, LoadCoverageCompleteAction, LoadMatchMetaDataAction as LoadFortniteMatchMetaDataAction, LoadMatchMetaDataCompleteAction as LoadFortniteMatchMetaDataCompleteAction, LoadMatchMetaDataOnceAction as LoadFortniteMatchMetaDataOnceAction, LoadGameAction, LoadGameCompleteAction, LoadGameOnceAction, LoadGameRulesAction, LoadGameRulesCompleteAction, LoadGameaccountAction, LoadGameaccountCompleteAction, LoadGameaccountDescriptionsAction, LoadGameaccountDescriptionsCompleteAction, LoadGameaccountOnceAction, LoadGameaccountProvidersAction, LoadGameaccountProvidersCompleteAction, LoadGeneralRulesAction, LoadGeneralRulesCompleteAction, LoadHelpAction, LoadHelpCompleteAction, LoadLeagueAction, LoadLeagueCompleteAction, LoadLeagueMatchesAction, LoadLeagueMatchesCompleteAction, LoadLeagueMatchesOnceAction, LoadLeagueOnceAction, LoadLeagueRestrictionsAction, LoadLeagueRestrictionsCompleteAction, LoadLeagueRestrictionsOnceAction, LoadLeagueResultsAction, LoadLeagueResultsCompleteAction, LoadLeagueResultsOnceAction, LoadLeaguesAction, LoadLeaguesCompleteAction, LoadLeaguesOnceAction, LoadMatchAction, LoadMatchChallengeReportAction, LoadMatchChallengeReportCompleteAction, LoadMatchChallengeReportOnceAction, LoadMatchCompleteAction, LoadMatchDateReportAction, LoadMatchDateReportCompleteAction, LoadMatchDateReportOnceAction, LoadMatchOnceAction, LoadMatchResultReportAction, LoadMatchResultReportCompleteAction, LoadMatchResultReportOnceAction, LoadMatchesAction, LoadMediaAction, LoadMediaCompleteAction, LoadMediaOnceAction, LoadMembersAction, LoadMembersCompleteAction, LoadMembersMatchesAction, LoadMembersMatchesCompleteAction, LoadMembersMatchesOnceAction, LoadMembersOnceAction, LoadMetaRankRankingAction, LoadMetaRankRankingCompleteAction, LoadParticipantsAction, LoadParticipantsCompleteAction, LoadParticipantsOnceAction, LoadPlayerAction, LoadPlayerCompleteAction, LoadPlayerLeaguesAction, LoadPlayerLeaguesCompleteAction, LoadPlayerLeaguesOnceAction, LoadPlayerMatchesAction, LoadPlayerMatchesCompleteAction, LoadPlayerMatchesOnceAction, LoadPlayerOnceAction, LoadPlayerShortDescriptionAction, LoadPlayerShortDescriptionCompleteAction, LoadPlayerShortDescriptionOnceAction, LoadPlayersAction, LoadPlayersCompleteAction, LoadPlayersOnceAction, LoadPrizesAction, LoadPrizesCompleteAction, LoadMatchMetaDataAction$2 as LoadPubgMatchMetaDataAction, LoadMatchMetaDataCompleteAction$2 as LoadPubgMatchMetaDataCompleteAction, LoadMatchMetaDataOnceAction$2 as LoadPubgMatchMetaDataOnceAction, LoadMatchSettingsAction as LoadPubgMatchSettingsAction, LoadMatchSettingsCompleteAction as LoadPubgMatchSettingsCompleteAction, LoadMatchSettingsOnceAction as LoadPubgMatchSettingsOnceAction, LoadMatchMetaDataAction$1 as LoadPubgmMatchMetaDataAction, LoadMatchMetaDataCompleteAction$1 as LoadPubgmMatchMetaDataCompleteAction, LoadMatchMetaDataOnceAction$1 as LoadPubgmMatchMetaDataOnceAction, LoadRoomDataAction as LoadPubgmRoomDataAction, LoadRoomDataCompleteAction as LoadPubgmRoomDataCompleteAction, LoadRoomDataOnceAction as LoadPubgmRoomDataOnceAction, LoadQuickRulesAction, LoadQuickRulesCompleteAction, LoadRosterAction, LoadRosterCompleteAction, LoadRosterHistoryAction, LoadRosterHistoryCompleteAction, LoadRosterHistoryOnceAction, LoadRosterOnceAction, LoadSponsorAction, LoadSponsorCompleteAction, LoadSystemRulesAction, LoadSystemRulesCompleteAction, LoadTeamAction, LoadTeamCompleteAction, LoadTeamOnceAction, LoadTeamsAction, LoadTeamsCompleteAction, LoadTeamsOnceAction, LoadUserAction, LoadUserCompleteAction, LoadUserPremiumAction, LoadUserPremiumCompleteAction, LoadUserShopDiscountCodesAction, LoadUserShopDiscountCodesCompleteAction, LoadUserTeamsAction, LoadUserTeamsCompleteAction, MatchActionTypes, MatchEffects, MatchModule, MatchSetupActionTypes, MatchSetupEffects, MetaRankActionTypes, MetaRankEffects, MetaRankModule, NamespaceEffects, PlayerActionTypes, PlayerEffects, PlayerModule, PubgModule, PubgmModule, RemoveMemberFromRosterAction, RosterActionTypes, RosterEffects, RosterModule, SPAMMY_ACTION_TYPES, SelectLeagueAction, SelectPlayerAction, SelectRosterAction, SelectTeamAction, SelectUserAction, StateModule, TeamActionTypes, TeamEffects, TeamModule, UserActionTypes, UserEffects, UserModule, autochessReducer, cocReducer, contestantReducer, createErrorObject, createLoadManySelector, createLoadReducer, createLoadSelector, createLoadingEntryState, createMultipleLoadReducer, createNestedMultipleLoadReducer, distinct, entryAvailable, entryExists, exists, fortniteReducer, gameAccountTypeReducer, gameReducer, gameaccountReducer, gameintegrationReducer, getAutochessMatch, getChatinfo, getContestantClan as getCocContestantClan, getContestant, getContestants, getCoverage, getMatchMetaData as getFortniteMatchMetaData, getGame, getGameAccountDescriptions, getGameAccountProviders, getGameRules, getGameaccounts, getGeneralRules, getHelp, getLeague, getLeagueParticipants, getLeagueRestrictions, getLeagueResults, getLeagues, getManyGameaccounts, getManyTeamMembers, getMatch, getMatchChallengeReport, getMatchDateReport, getMatchMedia, getMatchResultReport, getMetaRankRanking, getPlayer, getPlayers, getPrizes, getMatchMetaData$2 as getPubgMatchMetaData, getMatchSettings as getPubgMatchSettings, getMatchMetaData$1 as getPubgmMatchMetaData, getRoomData as getPubgmRoomData, getQuickRules, getRoster, getRosterHistories, getRosterHistory, getRosters, getRostersForTeams, getShortDescription, getSponsor, getSystemRules, getTeam$1 as getTeam, getTeamMemberMatches, getTeamMembers, getTeamMembersMatches, getTeams$1 as getTeams, getUser, getUserPremium, getUserShopDiscountCodes, getUserTeam, getUserTeams, leagueNamespaceReducer, leagueReducer, loadOnceActionType, matchReducer, matchSetupReducer, metarankReducer, normalizeContestant, normalizeMatch, normalizePlayerLeagues, normalizePlayerMatches, normalizeUserTeams, playerReducer, pubgReducer, pubgmReducer, queueReducers, reducers, rosterReducer, setToLoading, setToLoadingComplete, skipLoadOnceReducer, spammyActionType, teamReducer, type, userReducer };
//# sourceMappingURL=play-state.js.map
